import assert from "node:assert";
import crypto from "node:crypto";
import fs from "node:fs";
import http from "node:http";
import net from "node:net";
import os from "node:os";
import path from "node:path";
import { ReadableStream } from "node:stream/web";
import { setTimeout as wait } from "node:timers/promises";
import util from "node:util";
import zlib from "node:zlib";
import { checkMacOSVersion } from "@cloudflare/cli-shared-helpers";
import { removeDir, removeDirSync } from "@cloudflare/workers-utils/fs-helpers";
import { formatZodError } from "@cloudflare/workers-utils/zod-format";
import { $ as colors$, bold, dim, green, yellow } from "kleur/colors";
import stoppable from "stoppable";
import { getGlobalDispatcher, Pool } from "undici";
import SCRIPT_DEV_REGISTRY_PROXY from "worker:core/dev-registry-proxy";
import SCRIPT_MINIFLARE_SHARED from "worker:shared/index";
import SCRIPT_MINIFLARE_ZOD from "worker:shared/zod";
import { WebSocketServer } from "ws";
import { z } from "zod";
import { fallbackCf, setupCf } from "./cf";
import {
	isMiniflareUnsafeBinding,
	MiniflareOptionsSchema,
} from "./config/schema";
import { exitHook } from "./exit-hook";
import {
	coupleWebSocket,
	DispatchFetchDispatcher,
	fetch,
	getAccessibleHosts,
	getEntrySocketHttpOptions,
	Headers,
	Request,
	Response,
} from "./http";
import {
	D1_PLUGIN_NAME,
	DURABLE_OBJECTS_PLUGIN_NAME,
	FLAGSHIP_PLUGIN_NAME,
	getConnectSocketName,
	getDirectSocketName,
	getDurableObjectUniqueKey,
	getEmailPathsToClean,
	getEnvBindingsOfType,
	getExportsOfType,
	getGlobalServices,
	getPersistPath,
	getStorageScope,
	getTriggersOfType,
	HELLO_WORLD_PLUGIN_NAME,
	HOST_CAPNP_CONNECT,
	IMAGES_PLUGIN_NAME,
	KV_PLUGIN_NAME,
	launchBrowser,
	loadExternalPlugins,
	PLUGIN_ENTRIES,
	ProxyClient,
	ProxyNodeBinding,
	QUEUES_PLUGIN_NAME,
	QueuesError,
	R2_PLUGIN_NAME,
	RATELIMIT_PLUGIN_NAME,
	SECRET_STORE_PLUGIN_NAME,
	SERVICE_DEV_REGISTRY_PROXY,
	SERVICE_ENTRY,
	SOCKET_DEBUG_PORT,
	SOCKET_DEV_REGISTRY,
	SOCKET_ENTRY,
	SOCKET_ENTRY_LOCAL,
	STREAM_PLUGIN_NAME,
	WORKER_BINDING_SERVICE_LOOPBACK,
	WORKFLOWS_PLUGIN_NAME,
} from "./plugins";
import { RPC_PROXY_SERVICE_NAME } from "./plugins/assets/constants";
import { BROWSER_VERSION } from "./plugins/browser-rendering/browser-version";
import { closeBrowserProcess } from "./plugins/browser-rendering/process";
import {
	CUSTOM_SERVICE_KNOWN_OUTBOUND,
	CustomServiceKind,
	getUserServiceName,
	handlePrettyErrorRequest,
	JsonErrorSchema,
	reviveError,
} from "./plugins/core";
import { ContainerPrivilegesCache } from "./plugins/core/container";
import { InspectorProxyController } from "./plugins/core/inspector-proxy";
import { isModuleFallbackRequest } from "./plugins/core/module-fallback";
import { writeTempFile } from "./plugins/core/temp-file";
import { HyperdriveProxyController } from "./plugins/hyperdrive/hyperdrive-proxy";
import {
	cfImageLocalFetcher,
	imagesLocalFetcher,
} from "./plugins/images/fetcher";
import {
	HttpOptions_Style,
	kInspectorSocket,
	kVoid,
	Runtime,
	serializeConfig,
} from "./runtime";
import {
	isFileNotFoundError,
	MiniflareCoreError,
	NoOpLog,
	stripAnsi,
} from "./shared";
import { createDurableObjectStorageHandle } from "./shared/dev-control";
import {
	DevRegistry,
	getStorageCandidateName,
	getWorkerRegistry,
} from "./shared/dev-registry";
import {
	getOutboundDoProxyClassName,
	normaliseServiceDesignator,
} from "./shared/external-service";
import { isCompressedByCloudflareFL } from "./shared/mime-types";
import {
	canonicalisePersistRoot,
	withPersistRootStartupLock,
} from "./shared/persist-root-lock";
import {
	CacheHeaders,
	CoreBindings,
	CoreHeaders,
	decodeErrorPayload,
	LogLevel,
	Mutex,
	sanitisePath,
	SharedHeaders,
	SiteBindings,
} from "./workers";
import { ADMIN_API as FLAGSHIP_ADMIN_API } from "./workers/flagship/constants";
import { ADMIN_API } from "./workers/secrets-store/constants";
import type {
	MiniflareOptions,
	ParsedInstanceOptions,
	ParsedWorkerOptions,
} from "./config/schema";
import type { DispatchFetch, RequestInit } from "./http";
import type {
	DurableObjectClassNames,
	MiniflareFetcherBinding,
	Plugin,
	PluginServicesOptions,
	QueueConsumers,
	QueueProducers,
	ReplaceWorkersTypes,
} from "./plugins";
import type {
	Config,
	Extension,
	RuntimeOptions,
	Service,
	Socket,
	SocketIdentifier,
	SocketPorts,
	Worker_Binding,
	Worker_DurableObjectNamespace,
	Worker_Module,
} from "./runtime";
import type { Log } from "./shared";
import type {
	DevControl,
	DurableObjectEvictionOptions,
	DurableObjectStorageHandle,
	DurableObjectStorageOptions,
} from "./shared/dev-control";
import type { WorkerDefinition } from "./shared/dev-registry-types";
import type { Awaitable } from "./workers";
import type { FlagshipAdmin } from "./workers/flagship/admin";
import type { EvaluationDetails, FlagValue } from "./workers/flagship/evaluate";
import type { Flag, FlagInput } from "./workers/flagship/flags";
import type {
	CacheStorage,
	D1Database,
	DurableObjectNamespace,
	Fetcher,
	Flagship,
	ImagesBinding,
	KVNamespace,
	KVNamespaceListKey,
	Queue,
	R2Bucket,
	StreamBinding,
} from "@cloudflare/workers-types/experimental";
import type { Process } from "@puppeteer/browsers";
import type { Abortable } from "node:events";
import type { Duplex, Transform, Writable } from "node:stream";
import type { Dispatcher, Response as UndiciResponse } from "undici";

const DEFAULT_HOST = "127.0.0.1";
function getURLSafeHost(host: string) {
	return net.isIPv6(host) ? `[${host}]` : host;
}

function resolveLocalhost(host: string) {
	return host === "localhost" ? "127.0.0.1" : undefined;
}

function maybeGetLocallyAccessibleHost(
	h: string
): "localhost" | "127.0.0.1" | "[::1]" | undefined {
	if (h === "localhost") {
		return "localhost";
	}
	if (h === "127.0.0.1" || h === "*" || h === "0.0.0.0" || h === "::") {
		return "127.0.0.1";
	}
	if (h === "::1") {
		return "[::1]";
	}
}

/**
 * Normalizes a hostname for use in a client-reachable URL.
 *
 * - Maps wildcard/listen-all addresses to `127.0.0.1`.
 * - Brackets IPv6 addresses (e.g. `::1` -> `[::1]`, `fe80::1` -> `[fe80::1]`).
 * - Leaves hostnames and IPv4 addresses unchanged.
 */
export function getLocallyAccessibleHost(host: string): string {
	if (host === "0.0.0.0" || host === "::" || host === "*") {
		return "127.0.0.1";
	}
	return getURLSafeHost(host);
}

/**
 * Builds a client-reachable URL for a local server given host, port, and
 * secure flag. Handles IPv6 bracketing and wildcard-address normalization
 * so the resulting string is always a valid URL.
 */
export function buildPublicUrl(options: {
	hostname?: string;
	port: number;
	secure?: boolean;
}): string {
	const host = getLocallyAccessibleHost(options.hostname ?? "localhost");
	const protocol = options.secure ? "https" : "http";
	return `${protocol}://${host}:${options.port}`;
}

function getServerPort(server: http.Server) {
	const address = server.address();
	// Note address would be string with unix socket
	assert(address !== null && typeof address === "object");
	return address.port;
}

function validateOptions(
	opts: unknown
): [ParsedInstanceOptions, ParsedWorkerOptions[]] {
	let parsed: z.infer<typeof MiniflareOptionsSchema>;
	try {
		parsed = MiniflareOptionsSchema.parse(opts);
	} catch (e) {
		if (e instanceof z.ZodError) {
			let formatted: string | undefined;
			try {
				formatted = formatZodError(e, opts);
			} catch (formatError) {
				// If formatting failed for some reason, we'd like to know, so log a
				// bunch of debugging information, including the full validation error
				// so users at least know what was wrong.

				const title = "[Miniflare] Validation Error Format Failure";
				const message = [
					"### Input",
					"```",
					util.inspect(opts, { depth: null }),
					"```",
					"",
					"### Validation Error",
					"```",
					e.stack,
					"```",
					"",
					"### Format Error",
					"```",
					typeof formatError === "object" &&
					formatError !== null &&
					"stack" in formatError &&
					typeof formatError.stack === "string"
						? formatError.stack
						: String(formatError),
					"```",
				].join("\n");
				const githubIssueUrl = new URL(
					"https://github.com/cloudflare/miniflare/issues/new"
				);
				githubIssueUrl.searchParams.set("title", title);
				githubIssueUrl.searchParams.set("body", message);

				formatted = [
					"Unable to format validation error.",
					"Please open the following URL in your browser to create a GitHub issue:",
					githubIssueUrl,
					"",
					message,
					"",
				].join("\n");
			}
			const error = new MiniflareCoreError(
				"ERR_VALIDATION",
				`Unexpected options passed to \`new Miniflare()\` constructor:\n${formatted}`
			);
			// Add the `cause` as a getter, so it isn't logged automatically with the
			// error, but can still be accessed if needed
			Object.defineProperty(error, "cause", { get: () => e });
			throw error;
		}
		throw e;
	}

	const { workers, ...sharedOpts } = parsed;
	if (workers.length === 0) {
		throw new MiniflareCoreError("ERR_NO_WORKERS", "No workers defined");
	}

	// Validate names unique
	const names = new Set<string>();
	for (const workerOpts of workers) {
		const name = workerOpts.config.name;
		if (names.has(name)) {
			throw new MiniflareCoreError(
				"ERR_DUPLICATE_NAME",
				name === ""
					? "Multiple workers defined without a `name`"
					: `Multiple workers defined with the same \`name\`: "${name}"`
			);
		}
		names.add(name);
	}

	return [sharedOpts, workers];
}

// When creating user worker services, we need to know which Durable Object
// classes each worker defines. In the new config format, `config.exports` is a
// reliable map of every Durable Object class a worker hosts, so we collect them
// from there — we don't need to inspect `config.env`
// `env` is handled by `getBindings` in the Durable Objects plugin).
function getDurableObjectClassNames(
	allWorkerOpts: ParsedWorkerOptions[]
): DurableObjectClassNames {
	const serviceClassNames: DurableObjectClassNames = new Map();

	for (const workerOpts of allWorkerOpts) {
		const serviceName = getUserServiceName(workerOpts.config.name);
		let classNames = serviceClassNames.get(serviceName);
		if (classNames === undefined) {
			classNames = new Map();
			serviceClassNames.set(serviceName, classNames);
		}

		for (const [className, exported] of getExportsOfType(
			workerOpts.config,
			"durable-object"
		)) {
			classNames.set(className, {
				enableSql: exported.storage === "sqlite",
				unsafeUniqueKey: exported.unsafeUniqueKey,
				unsafePreventEviction: exported.unsafePreventEviction,
				container: exported.container,
			});
		}
	}

	return serviceClassNames;
}

/**
 * Collects all external service bindings from all workers and overrides
 * them to point to the dev registry proxy worker's ExternalServiceProxy
 * entrypoint. Returns a map of external service names to the entrypoints
 * and DO classes that are referenced.
 */
function getExternalServiceEntrypoints(allWorkerOpts: ParsedWorkerOptions[]) {
	const externalServices = new Map<
		string,
		{
			classNames: Set<string>;
			entrypoints: Set<string | undefined>;
		}
	>();
	const allWorkerNames = allWorkerOpts.map((opts) => opts.config.name);
	const getEntrypoints = (name: string) => {
		let externalService = externalServices.get(name);

		if (!externalService) {
			externalService = {
				classNames: new Set(),
				entrypoints: new Set(),
			};
			externalServices.set(name, externalService);
		}

		return externalService;
	};

	for (const workerOpts of allWorkerOpts) {
		const { config, dev } = workerOpts;

		// Tail consumers targeting a worker outside this instance. Reroute them
		// through the dev-registry proxy worker (`ExternalServiceProxy`), mirroring
		// the `worker`/`durable-object` reroute below. Without this, the worker
		// binds its tail to a non-existent local service `core:user:<workerName>`
		// and workerd refuses to start. Handled before the `env` guard since a
		// worker may declare tail consumers without any `env` bindings.
		const tailConsumers = config.tailConsumers;
		if (tailConsumers !== undefined) {
			for (let i = 0; i < tailConsumers.length; i++) {
				const consumer = tailConsumers[i];
				const serviceName = consumer.worker;
				if (serviceName && !allWorkerNames.includes(serviceName)) {
					getEntrypoints(serviceName).entrypoints.add(consumer.entrypoint);
					tailConsumers[i] = {
						worker: SERVICE_DEV_REGISTRY_PROXY,
						streaming: consumer.streaming,
						entrypoint: "ExternalServiceProxy",
						// User-supplied `props` are preserved in `userProps` so the proxy
						// can forward them to the remote entrypoint via the debug port.
						props: {
							service: serviceName,
							entrypoint: consumer.entrypoint ?? null,
							userProps: consumer.props,
						},
					};
				}
			}
		}

		const env = config.env;
		if (env === undefined) {
			continue;
		}

		// `worker` service bindings
		for (const [name, binding] of getEnvBindingsOfType(config, "worker")) {
			const { serviceName, entrypoint, props, remoteProxyConnectionString } =
				normaliseServiceDesignator(binding, dev);

			if (
				// Skip if it is a remote service
				remoteProxyConnectionString === undefined &&
				// Skip SELF (`kCurrentWorker`) and services bound to another Worker
				// defined in this Miniflare config
				serviceName &&
				!allWorkerNames.includes(serviceName)
			) {
				getEntrypoints(serviceName).entrypoints.add(entrypoint);
				// Reroute through the dev-registry proxy worker. User-supplied `props`
				// are preserved in `userProps` so the proxy can forward them to the
				// remote entrypoint via the debug port.
				env[name] = {
					type: "worker",
					worker: SERVICE_DEV_REGISTRY_PROXY,
					exportName: "ExternalServiceProxy",
					props: {
						service: serviceName,
						entrypoint: entrypoint ?? null,
						userProps: props,
					},
				};
			}
		}

		// `durable-object` bindings targeting a worker outside this instance
		for (const [bindingName, binding] of getEnvBindingsOfType(
			config,
			"durable-object"
		)) {
			const { worker, exportName } = binding;
			if (!allWorkerNames.includes(worker)) {
				// Point it at the outbound DO proxy class on the dev-registry proxy
				// worker. The proxy worker registers the namespace (with a matching
				// unique key) itself, so no extra config is needed on the binding.
				env[bindingName] = {
					type: "durable-object",
					worker: SERVICE_DEV_REGISTRY_PROXY,
					exportName: getOutboundDoProxyClassName(worker, exportName),
				};
				getEntrypoints(worker).classNames.add(exportName);
			}
		}

		// Cross-worker workflow bindings: when `worker` refers to a worker
		// outside this Miniflare instance (registered in the dev registry), record
		// its entrypoint so the dev-registry proxy exposes it. The workflows plugin
		// reroutes the engine's USER_WORKFLOW binding through the proxy itself; here
		// we only register the external entrypoint. Mirrors the DO block above.
		for (const [, binding] of getEnvBindingsOfType(config, "workflow")) {
			const { worker, exportName } = binding;
			if (!allWorkerNames.includes(worker)) {
				getEntrypoints(worker).entrypoints.add(exportName);
			}
		}
	}

	return externalServices;
}

function getQueueProducers(
	allWorkerOpts: ParsedWorkerOptions[]
): QueueProducers {
	const queueProducers: QueueProducers = new Map();
	for (const workerOpts of allWorkerOpts) {
		const workerName = workerOpts.config.name;
		for (const [bindingName, binding] of getEnvBindingsOfType(
			workerOpts.config,
			"queue"
		)) {
			queueProducers.set(bindingName, {
				workerName,
				queueName: binding.name ?? bindingName,
				deliveryDelay: binding.deliveryDelay,
			});
		}
	}
	return queueProducers;
}

function getQueueConsumers(
	allWorkerOpts: ParsedWorkerOptions[]
): QueueConsumers {
	const queueConsumers: QueueConsumers = new Map();
	for (const workerOpts of allWorkerOpts) {
		const workerName = workerOpts.config.name;
		for (const consumer of getTriggersOfType(workerOpts.config, "queue")) {
			const queueName = consumer.name;
			// Validate that each queue has at most one consumer...
			const existingConsumer = queueConsumers.get(queueName);
			if (existingConsumer !== undefined) {
				throw new QueuesError(
					"ERR_MULTIPLE_CONSUMERS",
					`Multiple consumers defined for queue "${queueName}": "${existingConsumer.workerName}" and "${workerName}"`
				);
			}
			// ...then store the consumer
			queueConsumers.set(queueName, {
				workerName,
				maxBatchSize: consumer.maxBatchSize,
				maxBatchTimeout: consumer.maxBatchTimeout,
				maxRetries: consumer.maxRetries,
				deadLetterQueue: consumer.deadLetterQueue,
				retryDelay: consumer.retryDelay,
			});
		}
	}

	for (const [queueName, consumer] of queueConsumers) {
		// Check the dead letter queue isn't configured to be the queue itself
		// (NOTE: Queues *does* permit DLQ cycles between multiple queues,
		//  i.e. if Q2 is DLQ for Q1, but Q1 is DLQ for Q2)
		if (consumer.deadLetterQueue === queueName) {
			throw new QueuesError(
				"ERR_DEAD_LETTER_QUEUE_CYCLE",
				`Dead letter queue for queue "${queueName}" cannot be itself`
			);
		}
	}

	return queueConsumers;
}

// Collects all routes from all worker services
function getWorkerRoutes(
	allWorkerOpts: ParsedWorkerOptions[]
): Map<string, string[]> {
	const allRoutes = new Map<string, string[]>();
	for (const workerOpts of allWorkerOpts) {
		const name = workerOpts.config.name;
		assert(!allRoutes.has(name)); // Validated unique names earlier
		allRoutes.set(
			name,
			getTriggersOfType(workerOpts.config, "fetch").map(
				(trigger) => trigger.pattern
			)
		);
	}
	return allRoutes;
}

// Get the name of a binding in the `ProxyServer`'s `env`
function getProxyBindingName(plugin: string, worker: string, binding: string) {
	return [
		CoreBindings.DURABLE_OBJECT_NAMESPACE_PROXY,
		plugin,
		worker,
		binding,
	].join(":");
}
// Get whether a binding will need a proxy to be supported in Node (i.e. is
// the implementation of this binding in `workerd`?). If this returns `false`,
// there's no need to bind the binding to the `ProxyServer`.
function isNativeTargetBinding(binding: Worker_Binding) {
	return !(
		"json" in binding ||
		"wasmModule" in binding ||
		"text" in binding ||
		"data" in binding
	);
}
// Converts a regular worker binding to binding suitable for the `ProxyServer`.
function buildProxyBinding(
	plugin: string,
	worker: string,
	binding: Worker_Binding
): Worker_Binding {
	assert(binding.name !== undefined);
	const name = getProxyBindingName(plugin, worker, binding.name);
	const proxyBinding = { ...binding, name };
	// If this is a Durable Object namespace binding to the current worker,
	// make sure it continues to point to that worker when bound elsewhere
	if (
		"durableObjectNamespace" in proxyBinding &&
		proxyBinding.durableObjectNamespace !== undefined
	) {
		proxyBinding.durableObjectNamespace.serviceName ??=
			getUserServiceName(worker);
	}
	return proxyBinding;
}
// Gets an array of proxy bindings for internal Durable Objects, only used in
// testing for accessing internal methods
function getInternalDurableObjectProxyBindings(
	plugin: string,
	service: Service
): Worker_Binding[] | undefined {
	if (!("worker" in service)) {
		return;
	}
	assert(service.worker !== undefined);
	const serviceName = service.name;
	assert(serviceName !== undefined);
	return service.worker.durableObjectNamespaces?.map(({ className }) => {
		assert(className !== undefined);
		return {
			name: getProxyBindingName(`${plugin}-internal`, serviceName, className),
			durableObjectNamespace: { serviceName, className },
		};
	});
}

type StoppableServer = http.Server & stoppable.WithStop;

const restrictedUndiciHeaders = [
	// From Miniflare 2:
	// https://github.com/cloudflare/miniflare/blob/9c135599dc21fe69080ada17fce6153692793bf1/packages/core/src/standards/http.ts#L129-L132
	"transfer-encoding",
	"connection",
	"keep-alive",
	"expect",
];
const restrictedWebSocketUpgradeHeaders = [
	"upgrade",
	"connection",
	"sec-websocket-accept",
];

export function _transformsForContentEncodingAndContentType(
	encoding: string | undefined,
	type: string | undefined | null
): Transform[] {
	const encoders: Transform[] = [];
	if (!encoding) {
		return encoders;
	}
	// if cloudflare's FL does not compress this mime-type, then don't compress locally either
	if (!isCompressedByCloudflareFL(type)) {
		return encoders;
	}

	// Reverse of https://github.com/nodejs/undici/blob/48d9578f431cbbd6e74f77455ba92184f57096cf/lib/fetch/index.js#L1660
	const codings = encoding
		.toLowerCase()
		.split(",")
		.map((x) => x.trim());
	for (const coding of codings) {
		if (/(x-)?gzip/.test(coding)) {
			encoders.push(zlib.createGzip());
		} else if (/(x-)?deflate/.test(coding)) {
			encoders.push(zlib.createDeflate());
		} else if (coding === "br") {
			encoders.push(zlib.createBrotliCompress());
		} else {
			// Unknown encoding, don't do any encoding at all
			encoders.length = 0;
			break;
		}
	}
	return encoders;
}

function safeReadableStreamFrom(iterable: AsyncIterable<Uint8Array>) {
	// Adapted from `undici`, catches errors from `next()` to avoid unhandled
	// rejections from aborted request body streams:
	// https://github.com/nodejs/undici/blob/dfaec78f7a29f07bb043f9006ed0ceb0d5220b55/lib/core/util.js#L369-L392
	let iterator: AsyncIterator<Uint8Array>;
	return new ReadableStream<Uint8Array>({
		async start() {
			iterator = iterable[Symbol.asyncIterator]();
		},
		// @ts-expect-error `pull` may return anything
		async pull(controller): Promise<boolean> {
			try {
				const { done, value } = await iterator.next();
				if (done) {
					queueMicrotask(() => controller.close());
				} else {
					const buf = Buffer.isBuffer(value) ? value : Buffer.from(value);
					controller.enqueue(new Uint8Array(buf));
				}
			} catch {
				queueMicrotask(() => controller.close());
			}
			// @ts-expect-error `pull` may return anything
			return controller.desiredSize > 0;
		},
		async cancel() {
			await iterator.return?.();
		},
	});
}

function extractCustomService(customService: string) {
	const slashIndex = customService.indexOf("/");
	// TODO: technically may want to keep old versions around so can always
	//  recover this in case of setOptions()?
	const workerIndex = parseInt(customService.substring(0, slashIndex));
	const serviceKind = customService[slashIndex + 1] as CustomServiceKind;
	const serviceName = customService.substring(slashIndex + 2);

	return { workerIndex, serviceKind, serviceName };
}

function getErrorMessage(error: unknown): string {
	if (error instanceof Error) {
		return error.stack || error.message;
	}

	if (typeof error === "string") {
		return error;
	}

	try {
		return JSON.stringify(error);
	} catch {
		return "Unknown error";
	}
}

// Maps `Miniflare` instances to stack traces for their construction. Used to identify un-`dispose()`d instances.
let maybeInstanceRegistry:
	| Map<Miniflare, string /* constructionStack */>
	| undefined;
/** @internal */
export function _initialiseInstanceRegistry() {
	return (maybeInstanceRegistry = new Map());
}

type PendingWorkflowStorageDelete = {
	promise: Promise<void>;
	failed: boolean;
	deleted: boolean;
};

/** Selects the Worker TCP trigger used by `Miniflare#dispatchConnect()`. */
export interface DispatchConnectOptions {
	/** Defaults to the entrypoint Worker. */
	workerName?: string;
	/** The configured trigger port, including `0` for an OS-assigned port. */
	port?: number;
}

const WORKFLOW_STORAGE_EXTENSIONS = [".sqlite", ".sqlite-shm", ".sqlite-wal"];
const WORKFLOW_STORAGE_DELETE_RETRY_INTERVAL_MS = 50;
const WORKFLOW_STORAGE_DELETE_TIMEOUT_MS = 2_000;
const WORKFLOW_STORAGE_DELETE_ATTEMPTS =
	WORKFLOW_STORAGE_DELETE_TIMEOUT_MS /
		WORKFLOW_STORAGE_DELETE_RETRY_INTERVAL_MS +
	1;

export class Miniflare {
	#previousSharedOpts?: ParsedInstanceOptions;
	#previousWorkerOpts?: ParsedWorkerOptions[];
	#sharedOpts: ParsedInstanceOptions;
	#workerOpts: ParsedWorkerOptions[];
	#log: Log;

	/**
	 * externalPlugins is a list of external plugins that have been loaded
	 * after being referenced by an unsafe binding
	 */
	#externalPlugins: Map<string, Plugin> = new Map();

	// key is the browser session ID, value identifies the launched browser
	#browserProcesses: Map<
		string,
		{ browserProcess: Process; wsEndpoint: string }
	> = new Map();
	#pendingWorkflowStorageDeletes = new Map<
		string,
		PendingWorkflowStorageDelete
	>();

	readonly #runtime?: Runtime;
	readonly #removeExitHook?: () => void;
	#runtimeEntryURL?: URL;
	publicUrl?: string;
	#socketPorts?: SocketPorts;
	#runtimeDispatcher?: Dispatcher;
	#dispatchConnectSockets = new Set<net.Socket>();
	#proxyClient?: ProxyClient;
	#runtimeRestartError?: MiniflareCoreError;
	// Number of times workerd has crashed and been restarted for this instance.
	// Reported to the user so a repeatedly-crashing runtime is distinguishable
	// from a one-off, and so the restart isn't silent.
	#workerdCrashCount = 0;

	#cfObject?: Record<string, any> = {};

	// Path to temporary directory for use as scratch space/"in-memory" Durable
	// Object storage. Note this may not exist, it's up to the consumers to
	// create this if needed. Deleted on `dispose()`.
	readonly #tmpPath: string;

	// Mutual exclusion lock for runtime operations (i.e. initialisation and
	// updating config). This essentially puts initialisation and future updates
	// in a queue, ensuring they're performed in calling order.
	readonly #runtimeMutex: Mutex;

	// Store `#init()` `Promise`, so we can propagate initialisation errors in
	// `ready`. We would have no way of catching these otherwise.
	// eslint-disable-next-line no-unused-private-class-members -- oxlint is wrong here, this variable _is_ used
	readonly #initPromise: Promise<void>;

	// Aborted when dispose() is called
	readonly #disposeController: AbortController;
	#loopbackServer?: StoppableServer;
	#loopbackHost?: string;
	readonly #webSocketServer: WebSocketServer;
	readonly #webSocketExtraHeaders: WeakMap<http.IncomingMessage, Headers>;
	readonly #devRegistry: DevRegistry;

	#maybeInspectorProxyController?: InspectorProxyController;
	#previousRuntimeInspectorPort?: number;

	#containerPrivilegesCache = new ContainerPrivilegesCache();
	#hyperdriveProxyController: HyperdriveProxyController =
		new HyperdriveProxyController();

	// Set the first time the remote-bindings proxy CLIENT worker reports a
	// Cloudflare Access 403 block on a request to the remote-bindings proxy
	// server. Used to dedupe the user-facing warning to once per session.
	#warnedRemoteBindingsAccessBlock = false;

	constructor(opts: MiniflareOptions) {
		// Split and validate options
		const [sharedOpts, workerOpts] = validateOptions(opts);

		checkMacOSVersion({ shouldThrow: true });

		this.#sharedOpts = sharedOpts;
		this.#workerOpts = workerOpts;

		const workerNamesToProxy = this.#workerNamesToProxy();
		const enableInspectorProxy = workerNamesToProxy.size > 0;

		if (enableInspectorProxy) {
			if (this.#sharedOpts.inspectorPort === undefined) {
				throw new MiniflareCoreError(
					"ERR_MISSING_INSPECTOR_PROXY_PORT",
					"inspector proxy requested but without an inspectorPort specified"
				);
			}
		}

		// Add to registry after initial options validation, before any servers/
		// child processes are started
		if (maybeInstanceRegistry !== undefined) {
			const object = { name: "Miniflare", stack: "" };
			Error.captureStackTrace(object, Miniflare);
			maybeInstanceRegistry.set(this, object.stack);
		}

		this.#log = this.#sharedOpts.log ?? new NoOpLog();
		this.#hyperdriveProxyController.log = this.#log;

		// If we're in a JavaScript Debug terminal, Miniflare will send the inspector ports directly to VSCode for registration
		// As such, we don't need our inspector proxy and in fact including it causes issue with multiple clients connected to the
		// inspector endpoint.
		const inVscodeJsDebugTerminal = !!process.env.VSCODE_INSPECTOR_OPTIONS;

		if (enableInspectorProxy && !inVscodeJsDebugTerminal) {
			if (this.#sharedOpts.inspectorPort === undefined) {
				throw new MiniflareCoreError(
					"ERR_MISSING_INSPECTOR_PROXY_PORT",
					"inspector proxy requested but without an inspectorPort specified"
				);
			}

			this.#maybeInspectorProxyController = new InspectorProxyController(
				this.#sharedOpts.inspectorPort,
				this.#sharedOpts.inspectorHost,
				this.#log,
				workerNamesToProxy
			);
		}

		this.#webSocketServer = new WebSocketServer({
			noServer: true,
			// Disable automatic handling of `Sec-WebSocket-Protocol` header,
			// Cloudflare Workers require users to include this header themselves in
			// `Response`s: https://github.com/cloudflare/miniflare/issues/179
			handleProtocols: () => false,
		});
		// Add custom headers included in response to WebSocket upgrade requests
		this.#webSocketExtraHeaders = new WeakMap();
		this.#webSocketServer.on("headers", (headers, req) => {
			const extra = this.#webSocketExtraHeaders.get(req);
			this.#webSocketExtraHeaders.delete(req);
			if (extra) {
				for (const [key, value] of extra) {
					if (!restrictedWebSocketUpgradeHeaders.includes(key.toLowerCase())) {
						headers.push(`${key}: ${value}`);
					}
				}
			}
		});

		this.#devRegistry = new DevRegistry(
			this.#sharedOpts.unsafeDevRegistryPath,
			(registry) => {
				void this.#queueRegistryUpdate();
				this.#sharedOpts.unsafeHandleDevRegistryUpdate?.(registry);
			},
			this.#log
		);

		// Build path for temporary directory. We don't actually want to create this
		// unless it's needed (i.e. we have Durable Objects enabled). This means we
		// can't use `fs.mkdtemp()`, as that always creates the directory.
		this.#tmpPath = path.join(
			os.tmpdir(),
			`miniflare-${crypto.randomBytes(16).toString("hex")}`
		);

		// Setup runtime
		this.#runtime = new Runtime();
		this.#removeExitHook = exitHook(() => {
			void this.#runtime?.dispose();
			// This exit hook is synchronous — the event loop will never run again
			// after it returns, so any operation that schedules a microtask (like
			// fs.promises.rm) will never even be executed, let alone completed.
			// We must use the sync variant here.
			// `Runtime#dispose()` should kill the runtime immediately but it might not,
			// so we only clean up on a best effort basis.
			try {
				removeDirSync(this.#tmpPath);
			} catch (e) {
				this.#log.debug(`Unable to remove temporary directory: ${String(e)}`);
			}
			// Clean up email session directories in the project temp path. When no
			// project temp path is supplied, these live inside `#tmpPath` and are
			// already removed above.
			const emailPaths = getEmailPathsToClean(
				this.#sharedOpts.resourceTmpPath,
				this.#tmpPath
			);
			if (emailPaths) {
				try {
					removeDirSync(emailPaths.sessionDir);
				} catch (e) {
					this.#log.debug(
						`Unable to remove email session directory: ${String(e)}`
					);
				}
			}
			// Unregister all workers from the dev registry. Note that dispose()
			// does synchronous cleanup (unregistering workers) then returns a
			// Promise for async cleanup (closing watcher, terminating proxy).
			// The .catch() will never run since the event loop won't tick again,
			// but the synchronous portion still executes.
			this.#devRegistry.dispose();
		});

		this.#disposeController = new AbortController();
		this.#runtimeMutex = new Mutex();
		this.#initPromise = this.#runtimeMutex
			.runWith(async () => {
				if (
					this.#sharedOpts.unsafeEnableSharedStorage &&
					this.#sharedOpts.resourcePersistencePath !== undefined
				) {
					this.#sharedOpts = {
						...this.#sharedOpts,
						resourcePersistencePath: await canonicalisePersistRoot(
							this.#sharedOpts.resourcePersistencePath
						),
					};
				}
				await this.#assembleAndUpdateConfig();
			})
			.catch((e) => {
				// If initialisation failed, attempting to `dispose()` this instance
				// will too. Therefore, remove from the instance registry now, so we
				// can still test async initialisation failures, without test failures
				// telling us to `dispose()` the instance.
				maybeInstanceRegistry?.delete(this);
				throw e;
			});
	}

	#workerNamesToProxy() {
		return new Set(
			this.#workerOpts
				.filter((w) => !!w.dev?.unsafeInspectorProxy)
				.map((w) => w.config.name)
		);
	}

	#handleReload() {
		// Close all existing web sockets on reload
		for (const ws of this.#webSocketServer.clients) {
			ws.close(1012, "Service Restart");
		}
	}

	/**
	 * Push updated registry data to the proxy worker via the entry socket.
	 * This updates the in-memory registry Map without restarting workerd.
	 * Called when the dev registry detects changes to external services.
	 */
	#devRegistryDispatcher?: Dispatcher;
	#devRegistryPort?: number;
	#registryPushPromise: Promise<void> = Promise.resolve();

	#queueRegistryUpdate(): Promise<void> {
		this.#registryPushPromise = this.#registryPushPromise.then(
			() => this.#pushRegistryUpdate(),
			() => this.#pushRegistryUpdate()
		);
		return this.#registryPushPromise;
	}

	async #pushRegistryUpdate(retries = 3): Promise<void> {
		if (this.#disposeController.signal.aborted) {
			return;
		}
		if (!this.#devRegistryDispatcher) {
			return;
		}
		// Always read the latest registry state rather than capturing a snapshot at
		// call-time: if the registry changes between the initial call and a retry,
		// the retry should push the most-recent state, not a stale one.
		const registry = this.#devRegistry.getRegistry();

		try {
			const response = await this.#devRegistryDispatcher.request({
				origin: "http://127.0.0.1",
				path: "/",
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(registry),
				signal: AbortSignal.timeout(2000),
			});
			// Drain the response body to release the connection back to the pool
			await response.body.dump();
			if (response.statusCode < 200 || response.statusCode >= 300) {
				this.#log.debug(`Registry push failed with ${response.statusCode}`);
				if (retries > 0) {
					await new Promise((r) => setTimeout(r, 500));
					return this.#pushRegistryUpdate(retries - 1);
				}
			}
		} catch (e: unknown) {
			this.#log.debug(
				`Failed to push registry update: ${
					e instanceof Error ? e.message : String(e)
				}`
			);
			if (retries > 0) {
				await new Promise((r) => setTimeout(r, 500));
				return this.#pushRegistryUpdate(retries - 1);
			}
		}
	}

	async #handleLoopbackCustomFetchService(
		request: Request,
		customService: string
	): Promise<Response> {
		let handler: MiniflareFetcherBinding["handler"] | undefined;
		// IMAGES_BINDING_SERVICE backs the Images binding (`env.IMAGES`).
		// IMAGES_FETCH_SERVICE backs `fetch(url, { cf: { image } })` transforms.
		if (customService === CoreBindings.IMAGES_BINDING_SERVICE) {
			handler = imagesLocalFetcher;
		} else if (customService === CoreBindings.IMAGES_FETCH_SERVICE) {
			handler = cfImageLocalFetcher;
		} else {
			const { workerIndex, serviceKind, serviceName } =
				extractCustomService(customService);
			const workerOpts = this.#workerOpts[workerIndex];
			if (serviceKind === CustomServiceKind.UNKNOWN) {
				const binding = workerOpts?.config.env?.[serviceName];
				if (binding?.type === "fetcher") {
					handler = binding.handler;
				}
			} else if (serviceName === CUSTOM_SERVICE_KNOWN_OUTBOUND) {
				const outbound = workerOpts?.dev?.outboundService;
				if (outbound?.type === "fetcher") {
					handler = outbound.handler;
				}
			}
		}
		// Should only define custom service bindings if `handler` is a function
		assert(typeof handler === "function");
		try {
			let response: UndiciResponse | Response = await handler(request, this);

			if (!(response instanceof Response)) {
				response = new Response(response.body, response);
			}

			// Validate return type as `service` is a user defined function
			// TODO: should we validate outside this try/catch?
			return z.instanceof(Response).parse(response);
		} catch (error) {
			// TODO: do we need to add `CF-Exception` header or something here?
			//  check what runtime does
			return new Response(getErrorMessage(error), { status: 500 });
		}
	}

	async #handleLoopbackCustomNodeService(
		req: http.IncomingMessage,
		res: http.ServerResponse,
		customService: string
	) {
		let handler:
			| ((
					req: http.IncomingMessage,
					res: http.ServerResponse,
					miniflare: Miniflare
			  ) => Awaitable<void>)
			| undefined;
		const { workerIndex, serviceKind, serviceName } =
			extractCustomService(customService);
		if (serviceKind === CustomServiceKind.UNKNOWN) {
			const binding = this.#workerOpts[workerIndex]?.config.env?.[serviceName];
			if (binding?.type === "node-handler") {
				handler = binding.handler;
			}
		} else if (serviceName === CUSTOM_SERVICE_KNOWN_OUTBOUND) {
			const outbound = this.#workerOpts[workerIndex]?.dev?.outboundService;
			if (outbound?.type === "node-handler") {
				handler = outbound.handler;
			}
		}
		assert(typeof handler === "function");

		try {
			await handler(req, res, this);
		} catch (error) {
			if (!res.headersSent) {
				res.writeHead(500);
			}
			res.end(getErrorMessage(error));
		}
	}

	/**
	 * Writes a request body to a temp file and responds with its on-disk path.
	 *
	 * By default the file is written to a single random path under this
	 * instance's temp directory. Callers use the reserved `email/` prefix namespace
	 * to select email destinations, which group files by session and mirror them
	 * into the project directory.
	 *
	 * @param url in format: /core/store-temp-file?prefix&extension[&id]
	 */
	async #handleLoopbackStoreTempFileRequest(
		request: Request,
		url: URL
	): Promise<Response> {
		const extension = url.searchParams.get("extension") ?? "txt";
		const rawPrefix = url.searchParams.get("prefix");
		const emailPrefix =
			rawPrefix !== null && rawPrefix.startsWith("email/")
				? rawPrefix.slice("email/".length)
				: undefined;
		const prefix =
			emailPrefix !== undefined
				? `email/${emailPrefix}`
				: rawPrefix
					? `files/${rawPrefix}`
					: "files";
		if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/u.test(extension)) {
			return new Response("Invalid temporary-file extension", { status: 400 });
		}
		const prefixParts = prefix.split("/");
		if (
			prefixParts.some(
				(part) =>
					part.length === 0 ||
					part === "." ||
					part === ".." ||
					!/^[A-Za-z0-9][A-Za-z0-9._-]*$/u.test(part)
			)
		) {
			return new Response("Invalid temporary-file prefix", { status: 400 });
		}

		const rawId = url.searchParams.get("id");
		const id = rawId === null ? crypto.randomUUID() : sanitisePath(rawId);
		const fileName = `${id}.${extension}`;
		const contents = new Uint8Array(await request.arrayBuffer());
		const filePath = await writeTempFile({
			tmpPath: this.#tmpPath,
			prefix,
			fileName,
			contents,
		});
		if (emailPrefix !== undefined) {
			const emailPaths = getEmailPathsToClean(
				this.#sharedOpts.resourceTmpPath,
				this.#tmpPath
			);
			if (emailPaths) {
				return new Response(
					await writeTempFile({
						tmpPath: emailPaths.sessionDir,
						prefix: emailPrefix,
						fileName,
						contents,
					}),
					{ status: 200 }
				);
			}
		}
		return new Response(filePath, { status: 200 });
	}

	/**
	 * Gets DO object IDs by checking filenames in the DO persistence directory.
	 *
	 * @param url in format: /core/do-storage/<namespaceId>
	 * @returns [{ name: string, type: "file" | "directory" }, ...]
	 */
	async #handleLoopbackDOStorageRequest(url: URL): Promise<Response> {
		// Extract namespace ID from path (e.g., "/core/do-storage/worker-TestDO" -> "worker-TestDO")
		const namespaceId = decodeURIComponent(
			url.pathname.slice("/core/do-storage/".length)
		);
		assert(namespaceId, "Namespace ID is required");

		const doPersistPath = getPersistPath(
			DURABLE_OBJECTS_PLUGIN_NAME,
			this.#tmpPath,
			this.#sharedOpts.isolatedResourcePersistencePath
		);

		const namespacePath = path.join(doPersistPath, namespaceId);

		// Prevent escaping directory through dodgy namespaceId that encodes (`../` etc.)
		// by ensuring the resolved path is still within the persistence directory
		if (!namespacePath.startsWith(path.resolve(doPersistPath) + path.sep)) {
			return new Response("Invalid namespace ID", { status: 400 });
		}

		try {
			const dirEntries = await fs.promises.readdir(namespacePath, {
				withFileTypes: true,
			});
			return Response.json(
				dirEntries.map((entry) => ({
					name: entry.name,
					type: entry.isDirectory() ? "directory" : "file",
				}))
			);
		} catch (e) {
			if (isFileNotFoundError(e)) {
				return new Response("Not Found", { status: 404 });
			}
			throw e;
		}
	}

	/**
	 * Lists Workflow Engine DO instances by reading filenames from the
	 * persistence directory. Same pattern as #handleLoopbackDOStorageRequest
	 * but also includes mtimeMs for sorting by most recently modified.
	 *
	 * @param url in format: /core/workflow-storage/<workflowName>
	 * @returns [{ name: string, type: "file" | "directory", mtimeMs: number }, ...]
	 */
	async #handleLoopbackWorkflowStorageRequest(url: URL): Promise<Response> {
		const workflowName = decodeURIComponent(
			url.pathname.slice("/core/workflow-storage/".length)
		);
		assert(workflowName, "Workflow name is required");

		const workflowsPersistPath = getPersistPath(
			WORKFLOWS_PLUGIN_NAME,
			this.#tmpPath,
			this.#sharedOpts.isolatedResourcePersistencePath
		);

		// Engine DOs are stored under: <persistPath>/miniflare-workflows-<name>/<hexId>.sqlite
		const uniqueKey = `miniflare-workflows-${workflowName}`;
		const namespacePath = path.join(workflowsPersistPath, uniqueKey);

		// Prevent directory traversal
		if (
			!namespacePath.startsWith(path.resolve(workflowsPersistPath) + path.sep)
		) {
			return new Response("Invalid workflow name", { status: 400 });
		}

		try {
			const dirEntries = await fs.promises.readdir(namespacePath, {
				withFileTypes: true,
			});
			// Include birthtimeMs so the handler can sort by file creation time
			// without resolving Engine DO metadata for every instance.
			const entries = await Promise.all(
				dirEntries.map(async (entry) => {
					let birthtimeMs = 0;
					if (entry.isFile()) {
						try {
							const stat = await fs.promises.stat(
								path.join(namespacePath, entry.name)
							);
							birthtimeMs = stat.birthtimeMs;
						} catch {
							// Ignore stat errors
						}
					}
					return {
						name: entry.name,
						type: entry.isDirectory()
							? ("directory" as const)
							: ("file" as const),
						birthtimeMs,
					};
				})
			);
			return Response.json(entries);
		} catch (e) {
			if (isFileNotFoundError(e)) {
				return new Response("Not Found", { status: 404 });
			}
			throw e;
		}
	}

	/** Removes an instance's SQLite files, retrying transient Windows locks. */
	async #deleteWorkflowStorageFiles(
		instancePath: string,
		pendingDelete: PendingWorkflowStorageDelete
	): Promise<void> {
		let firstError: unknown;
		let failed = false;
		for (const ext of WORKFLOW_STORAGE_EXTENSIONS) {
			const filePath = `${instancePath}${ext}`;
			for (
				let attempt = 0;
				attempt < WORKFLOW_STORAGE_DELETE_ATTEMPTS;
				attempt++
			) {
				try {
					await fs.promises.unlink(filePath);
					if (ext === ".sqlite") {
						pendingDelete.deleted = true;
					}
					break;
				} catch (error) {
					if (isFileNotFoundError(error)) {
						break;
					}
					const code =
						typeof error === "object" && error !== null && "code" in error
							? error.code
							: undefined;
					if (
						(code !== "EBUSY" && code !== "EPERM") ||
						attempt === WORKFLOW_STORAGE_DELETE_ATTEMPTS - 1
					) {
						if (!failed) {
							firstError = error;
							failed = true;
						}
						break;
					}
					await wait(WORKFLOW_STORAGE_DELETE_RETRY_INTERVAL_MS);
				}
			}
		}
		if (failed) {
			throw firstError;
		}
	}

	/** Runs a storage deletion after any earlier deletion for the same instance. */
	async #runWorkflowStorageDelete(
		instancePath: string,
		defer: boolean,
		pendingDelete: PendingWorkflowStorageDelete,
		previousDelete?: PendingWorkflowStorageDelete
	): Promise<void> {
		await previousDelete?.promise;
		pendingDelete.deleted = previousDelete?.deleted ?? false;
		if (defer) {
			await wait(100);
		}
		try {
			await this.#deleteWorkflowStorageFiles(instancePath, pendingDelete);
		} catch (error) {
			pendingDelete.failed = true;
			this.#log.error(
				error instanceof Error ? error : new Error(String(error))
			);
		}
		if (
			!pendingDelete.failed &&
			this.#pendingWorkflowStorageDeletes.get(instancePath) === pendingDelete
		) {
			this.#pendingWorkflowStorageDeletes.delete(instancePath);
		}
	}

	/** Serializes storage deletions for one instance path. */
	#queueWorkflowStorageDelete(
		instancePath: string,
		defer: boolean
	): PendingWorkflowStorageDelete {
		const previousDelete =
			this.#pendingWorkflowStorageDeletes.get(instancePath);
		const pendingDelete: PendingWorkflowStorageDelete = {
			deleted: false,
			failed: false,
			promise: Promise.resolve(),
		};
		this.#pendingWorkflowStorageDeletes.set(instancePath, pendingDelete);
		pendingDelete.promise = this.#runWorkflowStorageDelete(
			instancePath,
			defer,
			pendingDelete,
			previousDelete
		);
		return pendingDelete;
	}

	/** Waits for a queued storage deletion, retrying one failed deletion. */
	async #waitForWorkflowStorageDelete(
		instancePath: string,
		retried = false
	): Promise<Response> {
		const pendingDelete = this.#pendingWorkflowStorageDeletes.get(instancePath);
		if (pendingDelete === undefined) {
			return new Response(null, { status: 204 });
		}
		await pendingDelete.promise;

		const latestDelete = this.#pendingWorkflowStorageDeletes.get(instancePath);
		if (latestDelete === undefined) {
			return new Response(null, { status: 204 });
		}
		if (latestDelete !== pendingDelete) {
			return this.#waitForWorkflowStorageDelete(instancePath, retried);
		}
		if (!pendingDelete.failed) {
			this.#pendingWorkflowStorageDeletes.delete(instancePath);
			return new Response(null, { status: 204 });
		}
		if (retried || this.#disposeController.signal.aborted) {
			return new Response("Failed to delete workflow instance", {
				status: 500,
			});
		}

		this.#queueWorkflowStorageDelete(instancePath, false);
		return this.#waitForWorkflowStorageDelete(instancePath, true);
	}

	/**
	 * Deletes a Workflow Engine DO instance by removing its .sqlite file
	 * (and any associated -shm/-wal files) from the persistence directory.
	 *
	 * @param url in format: /core/workflow-storage/<workflowName>/<hexId>
	 */
	async #handleLoopbackWorkflowStorageDeleteRequest(
		url: URL
	): Promise<Response> {
		const pathAfterPrefix = url.pathname.slice(
			"/core/workflow-storage/".length
		);
		const slashIndex = pathAfterPrefix.indexOf("/");

		const workflowName = decodeURIComponent(
			slashIndex === -1 ? pathAfterPrefix : pathAfterPrefix.slice(0, slashIndex)
		);
		const hexId =
			slashIndex === -1
				? null
				: decodeURIComponent(pathAfterPrefix.slice(slashIndex + 1));

		assert(workflowName, "Workflow name is required");
		if (url.searchParams.has("waitForPendingDelete") && !hexId) {
			return new Response("Instance ID is required", { status: 400 });
		}

		const workflowsPersistPath = getPersistPath(
			WORKFLOWS_PLUGIN_NAME,
			this.#tmpPath,
			this.#sharedOpts.isolatedResourcePersistencePath
		);

		const uniqueKey = `miniflare-workflows-${workflowName}`;
		const namespacePath = path.join(workflowsPersistPath, uniqueKey);

		// Prevent directory traversal
		if (
			!namespacePath.startsWith(path.resolve(workflowsPersistPath) + path.sep)
		) {
			return new Response("Invalid workflow name", { status: 400 });
		}

		if (hexId) {
			const instancePath = path.join(namespacePath, hexId);
			if (!instancePath.startsWith(namespacePath + path.sep)) {
				return new Response("Invalid instance ID", { status: 400 });
			}

			if (url.searchParams.has("waitForPendingDelete")) {
				return this.#waitForWorkflowStorageDelete(instancePath);
			}

			const pendingDelete = this.#queueWorkflowStorageDelete(
				instancePath,
				url.searchParams.has("defer")
			);
			if (url.searchParams.has("defer")) {
				return new Response("Accepted", { status: 202 });
			}
			await pendingDelete.promise;
			if (pendingDelete.failed) {
				return new Response("Failed to delete workflow instance", {
					status: 500,
				});
			}
			if (!pendingDelete.deleted) {
				return new Response("Not Found", { status: 404 });
			}
		} else {
			// Delete ALL instances in the workflow
			try {
				const dirEntries = await fs.promises.readdir(namespacePath);
				await Promise.all(
					dirEntries
						.filter((name) =>
							WORKFLOW_STORAGE_EXTENSIONS.some((ext) => name.endsWith(ext))
						)
						.map((name) =>
							fs.promises.unlink(path.join(namespacePath, name)).catch(() => {})
						)
				);
			} catch (e) {
				if (!isFileNotFoundError(e)) {
					throw e;
				}
			}
		}

		return new Response("OK", { status: 200 });
	}

	#handleLoopback = async (
		req: http.IncomingMessage,
		res?: http.ServerResponse
	): Promise<Response | undefined> => {
		const customNodeService =
			req.headers[CoreHeaders.CUSTOM_NODE_SERVICE.toLowerCase()];
		if (typeof customNodeService === "string") {
			assert(res);
			this.#handleLoopbackCustomNodeService(req, res, customNodeService);
			return;
		}
		// Extract headers from request
		const headers = new Headers();
		for (const [name, values] of Object.entries(req.headers)) {
			// These headers are unsupported in undici fetch requests, they're added
			// automatically. For custom service bindings, we may pass this request
			// straight through to another fetch so strip them now.
			if (restrictedUndiciHeaders.includes(name)) {
				continue;
			}
			if (Array.isArray(values)) {
				for (const value of values) {
					headers.append(name, value);
				}
			} else if (values !== undefined) {
				headers.append(name, values);
			}
		}

		// Extract cf blob (if any) from headers
		const cfBlob = headers.get(CoreHeaders.CF_BLOB);
		headers.delete(CoreHeaders.CF_BLOB);
		assert(!Array.isArray(cfBlob)); // Only `Set-Cookie` headers are arrays
		const cf = cfBlob ? JSON.parse(cfBlob) : undefined;

		// Extract original URL passed to `fetch`
		const originalUrl = headers.get(CoreHeaders.ORIGINAL_URL);
		const url = new URL(originalUrl ?? req.url ?? "", "http://localhost");
		headers.delete(CoreHeaders.ORIGINAL_URL);

		const noBody = req.method === "GET" || req.method === "HEAD";
		const body = noBody ? undefined : safeReadableStreamFrom(req);
		const request = new Request(url, {
			method: req.method,
			headers,
			body,
			duplex: "half",
			cf,
		});

		let response: Response | undefined;
		try {
			const customFetchService = request.headers.get(
				CoreHeaders.CUSTOM_FETCH_SERVICE
			);
			if (customFetchService !== null) {
				request.headers.delete(CoreHeaders.CUSTOM_FETCH_SERVICE);
				response = await this.#handleLoopbackCustomFetchService(
					request,
					customFetchService
				);
			} else if (url.pathname === "/core/error") {
				response = await handlePrettyErrorRequest(
					this.#log,
					this.#workerOpts,
					request,
					this.#sharedOpts.handleUncaughtError
				);
			} else if (url.pathname === "/core/log") {
				const level = parseInt(
					request.headers.get(SharedHeaders.LOG_LEVEL) ?? "NaN"
				);
				assert(
					LogLevel.NONE <= level && level <= LogLevel.VERBOSE,
					`Expected ${SharedHeaders.LOG_LEVEL} header to be log level, got ${level}`
				);
				const logLevel = level as LogLevel;
				let message = await request.text();
				if (!colors$.enabled) {
					message = stripAnsi(message);
				}
				this.#log.logWithLevel(logLevel, message);
				response = new Response(null, { status: 204 });
			} else if (url.pathname === "/core/remote-bindings-access-warning") {
				// Reported by the remote-bindings proxy CLIENT worker when it sees
				// a 403 + "Cloudflare Access" body from the remote-bindings proxy
				// server. Surfaced once per Miniflare instance so users with many
				// remote bindings don't get a wall of repeated warnings.
				if (!this.#warnedRemoteBindingsAccessBlock) {
					this.#warnedRemoteBindingsAccessBlock = true;
					const bindingName = request.headers.get("MF-Binding") ?? "<unknown>";
					const proxyUrl = request.headers.get("MF-Proxy-URL") ?? "<unknown>";
					// Visually striking format so the warning isn't lost in the
					// noise of binding error stack traces that follow it.
					const separator = dim("━".repeat(76));
					this.#log.warn(
						`\n${separator}\n` +
							`${bold(
								yellow("Cloudflare Access blocked a remote bindings request")
							)}\n` +
							`${separator}\n` +
							`\n` +
							`Remote binding "${bold(
								bindingName
							)}": request to ${proxyUrl} was blocked.\n` +
							`\n` +
							`If your Cloudflare account protects workers.dev with Access, set the\n` +
							`${bold("CLOUDFLARE_ACCESS_CLIENT_ID")} and ${bold(
								"CLOUDFLARE_ACCESS_CLIENT_SECRET"
							)}\n` +
							`environment variables (Service Token credentials), or run\n` +
							`  ${bold(
								"cloudflared access login <your-workers.dev-host>"
							)}\n` +
							`for interactive authentication.\n` +
							`\n` +
							`See https://developers.cloudflare.com/cloudflare-one/access-controls/service-credentials/service-tokens/\n` +
							separator
					);
				}
				response = new Response(null, { status: 204 });
			} else if (url.pathname === "/browser/launch") {
				const headful = this.#workerOpts.some((w) =>
					getEnvBindingsOfType(w.config, "browser").some(
						([, binding]) => "headful" in binding && binding.headful
					)
				);
				const { sessionId, browserProcess, startTime, wsEndpoint } =
					await launchBrowser({
						browserVersion: BROWSER_VERSION,
						log: this.#log,
						tmpPath: this.#tmpPath,
						headful,
					});
				browserProcess.nodeProcess.on("exit", () => {
					this.#browserProcesses.delete(sessionId);
				});
				this.#browserProcesses.set(sessionId, {
					browserProcess,
					wsEndpoint,
				});
				response = Response.json({ wsEndpoint, sessionId, startTime });
			} else if (url.pathname === "/browser/status") {
				const sessionId = url.searchParams.get("sessionId");
				assert(sessionId !== null, "Missing sessionId query parameter");
				const browser = this.#browserProcesses.get(sessionId);
				response = new Response(null, { status: browser ? 200 : 410 });
			} else if (url.pathname === "/browser/close") {
				const sessionId = url.searchParams.get("sessionId");
				assert(sessionId !== null, "Missing sessionId query parameter");
				const browser = this.#browserProcesses.get(sessionId);
				if (!browser) {
					response = new Response("Session not found", { status: 404 });
				} else {
					this.#browserProcesses.delete(sessionId);
					await closeBrowserProcess(
						browser.browserProcess,
						browser.wsEndpoint
					).catch(() => {
						// oh well, process might already be dead
					});
					response = new Response(null, { status: 200 });
				}
			} else if (url.pathname === "/browser/sessionIds") {
				const sessionIds = this.#browserProcesses.keys();
				response = Response.json(Array.from(sessionIds));
			} else if (url.pathname === "/core/store-temp-file") {
				response = await this.#handleLoopbackStoreTempFileRequest(request, url);
			} else if (url.pathname.startsWith("/core/do-storage/")) {
				response = await this.#handleLoopbackDOStorageRequest(url);
			} else if (url.pathname.startsWith("/core/workflow-storage/")) {
				if (
					request.method === "DELETE" ||
					url.searchParams.has("waitForPendingDelete")
				) {
					response =
						await this.#handleLoopbackWorkflowStorageDeleteRequest(url);
				} else {
					response = await this.#handleLoopbackWorkflowStorageRequest(url);
				}
			} else if (url.pathname === "/core/dev-registry") {
				// Used by the local explorer to aggregate resources across instances
				const registryPath = this.#devRegistry.getRegistryPath();
				const registry = registryPath ? getWorkerRegistry(registryPath) : {};
				response = Response.json(registry, {
					headers: {
						"X-Miniflare-Dev-Registry-Instance-Id":
							this.#devRegistry.instanceId,
					},
				});
			} else if (url.pathname === "/core/public-url") {
				// Returns the public URL for this Miniflare instance. If a publicUrl
				// has been set (e.g. the Vite dev server URL), use that; otherwise
				// fall back to the runtime entry URL. This allows workers (e.g. the
				// stream binding) to construct externally-reachable URLs.
				response = Response.json(
					this.publicUrl ?? this.#runtimeEntryURL?.toString() ?? null
				);
			} else if (
				// Module fallback check MUST come after all known pathname handlers.
				// The V2 protocol (new_module_registry compat flag) uses POST requests,
				// which would otherwise match internal loopback requests from embedded
				// workers (e.g., POST to /core/log, /core/error, /core/store-temp-file).
				// By checking module fallback last, we ensure internal endpoints are
				// handled first, and only truly unmatched requests go to the fallback.
				this.#sharedOpts.unsafeModuleFallbackService !== undefined &&
				isModuleFallbackRequest(request) &&
				originalUrl === null
			) {
				response = await this.#sharedOpts.unsafeModuleFallbackService(
					request,
					this
				);
			}
		} catch (e: any) {
			this.#log.error(e);
			res?.writeHead(500);
			res?.end(e?.stack ?? String(e));
			return;
		}

		if (res !== undefined) {
			if (response === undefined) {
				res.writeHead(404);
				res.end();
			} else {
				await this.#writeResponse(response, res);
			}
		}

		return response;
	};

	#handleLoopbackUpgrade = async (
		req: http.IncomingMessage,
		socket: Duplex,
		head: Buffer
	) => {
		// Try handle the request in a worker
		const response = await this.#handleLoopback(req);

		// Check web socket response was returned
		const webSocket = response?.webSocket;
		if (response?.status === 101 && webSocket) {
			// Accept and couple the Web Socket
			this.#webSocketExtraHeaders.set(req, response.headers);
			this.#webSocketServer.handleUpgrade(req, socket, head, (ws) => {
				void coupleWebSocket(ws, webSocket);
				this.#webSocketServer.emit("connection", ws, req);
			});
			return;
		}

		// Otherwise, we'll be returning a regular HTTP response
		const res = new http.ServerResponse(req);
		// `socket` is guaranteed to be an instance of `net.Socket`:
		// https://nodejs.org/api/http.html#event-upgrade_1
		assert(socket instanceof net.Socket);
		res.assignSocket(socket);

		// If no response was provided, or it was an "ok" response, log an error
		if (!response || response.ok) {
			res.writeHead(500);
			res.end();
			this.#log.error(
				new TypeError(
					"Web Socket request did not return status 101 Switching Protocols response with Web Socket"
				)
			);
			return;
		}

		// Otherwise, send the response as is (e.g. unauthorised)
		await this.#writeResponse(response, res);
	};

	async #writeResponse(response: Response, res: http.ServerResponse) {
		// Convert headers into Node-friendly format
		const headers: http.OutgoingHttpHeaders = {};
		for (const entry of response.headers) {
			const key = entry[0].toLowerCase();
			const value = entry[1];
			if (key === "set-cookie") {
				headers[key] = response.headers.getSetCookie();
			} else {
				headers[key] = value;
			}
		}

		// If a `Content-Encoding` header is set, we'll need to encode the body
		// (likely only set by custom service bindings)
		const encoding = headers["content-encoding"]?.toString();
		const type = headers["content-type"]?.toString();
		const encoders = _transformsForContentEncodingAndContentType(
			encoding,
			type
		);
		if (encoders.length > 0) {
			// `Content-Length` if set, will be wrong as it's for the decoded length
			delete headers["content-length"];
		}

		res.writeHead(response.status, response.statusText, headers);

		// `initialStream` is the stream we'll write the response to. It
		// should end up as the first encoder, piping to the next encoder,
		// and finally piping to the response:
		//
		// encoders[0] (initialStream) -> encoders[1] -> res
		//
		// Not using `pipeline(passThrough, ...encoders, res)` here as that
		// gives a premature close error with server sent events. This also
		// avoids creating an extra stream even when we're not encoding.
		let initialStream: Writable = res;
		for (let i = encoders.length - 1; i >= 0; i--) {
			encoders[i].pipe(initialStream);
			initialStream = encoders[i];
		}

		// Response body may be null if empty
		if (response.body) {
			try {
				for await (const chunk of response.body) {
					if (chunk) {
						initialStream.write(chunk);
					}
				}
			} catch (error) {
				this.#log.debug(
					`Error writing response body, closing response early: ${error}`
				);
			}
		}

		initialStream.end();
	}

	async #getLoopbackPort(): Promise<number> {
		// This function must be run with `#runtimeMutex` held

		// Start loopback server (how the runtime accesses Node.js) using the same
		// host as the main runtime server.
		const configuredHost = this.#sharedOpts.host ?? DEFAULT_HOST;
		const loopbackHost = resolveLocalhost(configuredHost) ?? configuredHost;
		// If we've already started the loopback server...
		if (this.#loopbackServer !== undefined) {
			// ...and it's using the correct host, reuse it
			if (this.#loopbackHost === loopbackHost) {
				return getServerPort(this.#loopbackServer);
			}
			// Otherwise, stop it, and create a new one
			await this.#stopLoopbackServer();
		}
		this.#loopbackServer = await this.#startLoopbackServer(loopbackHost);
		this.#loopbackHost = loopbackHost;
		return getServerPort(this.#loopbackServer);
	}

	#startLoopbackServer(hostname: string): Promise<StoppableServer> {
		if (hostname === "*") {
			hostname = "::";
		}

		return new Promise((resolve, reject) => {
			const server = stoppable(
				http.createServer(this.#handleLoopback),
				/* grace */ 0
			);
			// Disable the idle keep-alive timeout for local dev — workerd pools
			// and reuses connections to the loopback server, and Node's default
			// `keepAliveTimeout` (5s) races with that reuse: Node closes an idle
			// pooled socket just as workerd sends the next request on it, which
			// surfaces in the Worker as "Network connection lost". This mirrors
			// the undici pools used for dispatch in the opposite direction, which
			// already disable their timeouts.
			server.keepAliveTimeout = 0;
			server.on("upgrade", this.#handleLoopbackUpgrade);
			const onError = (error: Error) => {
				server.close();
				reject(error);
			};
			server.once("error", onError);
			server.listen(0, hostname, () => {
				server.off("error", onError);
				// Startup has settled, so report operational errors through the logger
				server.on("error", (error) => this.#log.error(error));
				resolve(server);
			});
		});
	}

	#stopLoopbackServer(): Promise<void> {
		const loopbackServer = this.#loopbackServer;
		if (loopbackServer === undefined) {
			return Promise.resolve();
		}
		return new Promise((resolve, reject) => {
			loopbackServer.stop((err) => {
				if (err) {
					reject(err);
					return;
				}
				this.#loopbackServer = undefined;
				this.#loopbackHost = undefined;
				resolve();
			});
		});
	}

	#getSocketAddress(
		id: SocketIdentifier,
		previousRequestedPort: number | undefined,
		host = DEFAULT_HOST,
		requestedPort?: number,
		reusePort = false
	) {
		if (reusePort) {
			requestedPort = this.#socketPorts?.get(id) ?? requestedPort;
		}
		// If `port` is set to `0`, was previously set to `0`, and we previously had
		// a port for this socket, reuse that random port
		if (requestedPort === 0 && previousRequestedPort === 0) {
			requestedPort = this.#socketPorts?.get(id);
		}
		// Otherwise, default to a new random port
		return `${getURLSafeHost(host)}:${requestedPort ?? 0}`;
	}

	/**
	 * Load all external plugins referenced by any unsafe bindings, and store them on the class
	 * Loaded plugins are preserved across runtime reloads, and so should only be loaded once per binding
	 */
	async #loadExternalPlugins(workers: ParsedWorkerOptions[]): Promise<void> {
		const requestedExternalPlugins = new Map<
			/* plugin name */ string,
			/* package name */ string
		>();

		// De-duplicate requested external plugins across all Worker bindings.
		// Unsafe bindings live in `config.env` with a templated `unsafe:*` type and
		// carry the plugin reference under `dev.plugin`.
		for (const worker of workers) {
			for (const binding of Object.values(worker.config.env ?? {})) {
				if (isMiniflareUnsafeBinding(binding) && binding.dev?.plugin) {
					requestedExternalPlugins.set(
						binding.dev.plugin.name,
						binding.dev.plugin.package
					);
				}
			}
		}

		for (const [pluginName, packageName] of requestedExternalPlugins) {
			// Only load "new" plugins. They'll be cached across Worker reloads
			if (!this.#externalPlugins.has(pluginName)) {
				const providedPlugins = await loadExternalPlugins(packageName);

				// While this package can provide multiple plugins, make sure it at least provides the requested one
				if (!providedPlugins[pluginName]) {
					throw new MiniflareCoreError(
						"ERR_PLUGIN_LOADING_FAILED",
						`Package ${packageName} did not provide the plugin '${pluginName}'.`
					);
				}

				// Load all plugins provided by this package, even if not requested, as they might be requested by a different Worker/binding.
				for (const [name, plugin] of Object.entries(providedPlugins)) {
					this.#externalPlugins.set(name, plugin);
				}
			}
		}
	}

	async #assembleConfig(
		loopbackHost: string,
		loopbackPort: number,
		reusePorts: boolean
	): Promise<Config> {
		const allPreviousWorkerOpts = this.#previousWorkerOpts;
		const allWorkerOpts = this.#workerOpts;
		const sharedOpts = this.#sharedOpts;

		await this.#loadExternalPlugins(allWorkerOpts);

		sharedOpts.cf = await setupCf(this.#log, sharedOpts.cf);
		this.#cfObject = sharedOpts.cf;

		const externalServices = this.#devRegistry.isEnabled()
			? getExternalServiceEntrypoints(allWorkerOpts)
			: null;

		const durableObjectClassNames = getDurableObjectClassNames(allWorkerOpts);
		const queueProducers = getQueueProducers(allWorkerOpts);
		const queueConsumers = getQueueConsumers(allWorkerOpts);
		// When the dev registry is enabled, queue brokers bind to the dev-registry
		// proxy so they can deliver to consumers in other `wrangler dev` processes
		// (see `ExternalQueueProxy`), so the proxy worker must exist whenever
		// there are queues.
		const hasQueues = queueProducers.size > 0 || queueConsumers.size > 0;
		const allWorkerRoutes = getWorkerRoutes(allWorkerOpts);
		const workerNames = [...allWorkerRoutes.keys()];

		// Use Map to dedupe services by name
		const services = new Map<string, Service>();
		const extensions: Extension[] = [
			{
				modules: [
					{ name: "miniflare:shared", esModule: SCRIPT_MINIFLARE_SHARED() },
					{ name: "miniflare:zod", esModule: SCRIPT_MINIFLARE_ZOD() },
				],
			},
		];

		const sockets: Socket[] = [
			{
				name: SOCKET_ENTRY,
				service: { name: SERVICE_ENTRY },
				...(await getEntrySocketHttpOptions(sharedOpts)),
			},
		];
		const configuredHost = sharedOpts.host ?? DEFAULT_HOST;
		if (maybeGetLocallyAccessibleHost(configuredHost) === undefined) {
			// If we aren't able to locally access `workerd` on the configured host, configure an additional socket that's
			// only accessible on `127.0.0.1`
			sockets.push({
				name: SOCKET_ENTRY_LOCAL,
				service: { name: SERVICE_ENTRY },
				http: {},
				address: this.#getSocketAddress(
					SOCKET_ENTRY_LOCAL,
					undefined,
					"127.0.0.1",
					undefined,
					reusePorts
				),
			});
		}

		/**
		 * Bindings for `ProxyServer` Durable Object for the magic proxy and local explorer.
		 * Contains all workerd-native bindings.
		 */
		const proxyBindings: Worker_Binding[] = [];

		for (const [, plugin] of this.#mergedPluginEntries) {
			const pluginExtensions = await plugin.getExtensions?.({
				options: allWorkerOpts,
			});
			if (pluginExtensions) {
				extensions.push(...pluginExtensions);
			}
		}

		for (
			let workerIndex = 0;
			workerIndex < allWorkerOpts.length;
			workerIndex++
		) {
			const previousWorkerOpts = allPreviousWorkerOpts?.[workerIndex];
			const workerOpts = allWorkerOpts[workerIndex];
			const workerName = workerOpts.config.name;
			// Service-worker format workers provide a raw script; everything else is
			// a modules worker.
			const isModulesWorker =
				workerOpts.legacy?.serviceWorkerScript === undefined;

			// Collect all bindings from this worker
			const workerBindings: Worker_Binding[] = [];
			const additionalModules: Worker_Module[] = [];

			for (const [key, plugin] of this.#mergedPluginEntries) {
				const pluginBindings = await plugin.getBindings(
					workerOpts,
					sharedOpts,
					workerIndex
				);
				if (pluginBindings !== undefined) {
					for (const binding of pluginBindings) {
						// If this is the Workers Sites manifest, we need to add it as a
						// module for modules workers. For all other bindings, and in
						// service workers, just add to worker bindings.
						if (
							key === "kv" &&
							binding.name === SiteBindings.JSON_SITE_MANIFEST &&
							isModulesWorker
						) {
							assert("json" in binding && binding.json !== undefined);
							additionalModules.push({
								name: SiteBindings.JSON_SITE_MANIFEST,
								text: binding.json,
							});
						} else {
							workerBindings.push(binding);
						}

						// Only `workerd` native bindings need to be proxied, the rest are
						// already supported by Node.js (e.g. json, text/data blob, wasm)
						if (isNativeTargetBinding(binding)) {
							proxyBindings.push(buildProxyBinding(key, workerName, binding));
						}
						if ("service" in binding) {
							const targetWorkerName = binding.service?.name?.replace(
								"core:user:",
								""
							);

							/*
							 * If we are running multiple Workers in a single dev session,
							 * and this is a binding to a Worker with assets, we want that
							 * binding to point to the (assets) RPC Proxy Worker
							 */
							const maybeAssetTargetService = allWorkerOpts.find(
								(worker) =>
									worker.config.name === targetWorkerName &&
									worker.config.assets
							);
							if (maybeAssetTargetService && !binding.service?.entrypoint) {
								assert(binding.service?.name);
								binding.service.name = `${RPC_PROXY_SERVICE_NAME}:${targetWorkerName}`;
							}
						}
					}
				}
			}

			// Collect all services required by this worker
			const unsafeEphemeralDurableObjects =
				workerOpts.dev?.unsafeEphemeralDurableObjects ?? false;
			// Store publicUrl so the /core/public-url loopback route can return it.
			// This is set here (rather than only via the setter) so that the initial
			// value from MiniflareOptions is picked up on first startup.
			this.publicUrl = sharedOpts.publicUrl;

			const pluginServicesOptionsBase: Omit<
				PluginServicesOptions,
				"options" | "sharedOptions" | "devRegistryEnabled"
			> = {
				log: this.#log,
				workerBindings,
				workerIndex,
				additionalModules,
				tmpPath: this.#tmpPath,
				workerNames,
				loopbackHost,
				loopbackPort,
				durableObjectClassNames,
				unsafeEphemeralDurableObjects,
				queueProducers,
				queueConsumers,
				containerPrivilegesCache: this.#containerPrivilegesCache,
				hyperdriveProxyController: this.#hyperdriveProxyController,
			};
			for (const [key, plugin] of this.#mergedPluginEntries) {
				const pluginServicesExtensions = await plugin.getServices({
					...pluginServicesOptionsBase,
					options: workerOpts,
					sharedOptions: sharedOpts,
					devRegistryEnabled: this.#devRegistry.isEnabled(),
				});
				if (pluginServicesExtensions !== undefined) {
					let pluginServices: Service[];
					if (Array.isArray(pluginServicesExtensions)) {
						pluginServices = pluginServicesExtensions;
					} else {
						pluginServices = pluginServicesExtensions.services;
						extensions.push(...pluginServicesExtensions.extensions);
					}

					for (const service of pluginServices) {
						if (service.name !== undefined && !services.has(service.name)) {
							services.set(service.name, service);
							if (key !== DURABLE_OBJECTS_PLUGIN_NAME) {
								const maybeBindings = getInternalDurableObjectProxyBindings(
									key,
									service
								);
								if (maybeBindings !== undefined) {
									proxyBindings.push(...maybeBindings);
								}
							}
						}
					}
				}
			}

			// Allow additional sockets to be opened directly to specific workers,
			// bypassing Miniflare's entry worker.
			const previousDirectSockets =
				previousWorkerOpts?.dev?.unsafeDirectSockets ?? [];
			const directSockets = workerOpts.dev?.unsafeDirectSockets ?? [];
			for (let i = 0; i < directSockets.length; i++) {
				const previousDirectSocket = previousDirectSockets[i];
				const directSocket = directSockets[i];
				const serviceName = directSocket.serviceName ?? workerName;
				const entrypoint = directSocket.entrypoint ?? "default";
				const name = getDirectSocketName(workerIndex, entrypoint);
				const address = this.#getSocketAddress(
					name,
					previousDirectSocket?.port,
					directSocket.host,
					directSocket.port,
					reusePorts
				);
				// check if Worker with assets with default export
				// (class or non-class based)
				const service =
					workerOpts.config.assets && entrypoint === "default"
						? {
								name: `${RPC_PROXY_SERVICE_NAME}:${workerOpts.config.name}`,
							}
						: {
								name: getUserServiceName(serviceName),
								entrypoint: entrypoint === "default" ? undefined : entrypoint,
							};

				sockets.push({
					name,
					address,
					service,
					http: {
						style: directSocket.proxy ? HttpOptions_Style.PROXY : undefined,
						cfBlobHeader: CoreHeaders.CF_BLOB,
						capnpConnectHost: HOST_CAPNP_CONNECT,
					},
				});
			}

			// Open raw listening sockets that deliver incoming connections
			// to this Worker's `connect()`.
			const connectHandlers = getTriggersOfType(workerOpts.config, "connect");
			for (let j = 0; j < connectHandlers.length; j++) {
				const connectHandler = connectHandlers[j];
				// The socket's name already encodes the configured protocol/port, so
				// we can pass `connectHandler.port` as both the current and
				// "previous" port: `#getSocketAddress()` only compares these to
				// detect the `port: 0` (OS-assigned) case, in which case it looks up
				// the actual previously-assigned random port by this same `name`.
				const name = getConnectSocketName(
					workerIndex,
					connectHandler.protocol,
					connectHandler.port
				);
				const address = this.#getSocketAddress(
					name,
					connectHandler.port,
					connectHandler.address,
					connectHandler.port,
					reusePorts
				);

				sockets.push({
					name,
					address,
					service: { name: getUserServiceName(workerName) },
					tcp: {},
				});
			}
		}

		if (
			this.#devRegistry.isEnabled() &&
			externalServices !== null &&
			(externalServices.size > 0 ||
				hasQueues ||
				sharedOpts.unsafeEnableSharedStorage)
		) {
			await this.#devRegistry.watch(
				externalServices,
				hasQueues,
				sharedOpts.unsafeEnableSharedStorage === true &&
					sharedOpts.resourcePersistencePath !== undefined
			);

			const externalObjects = Array.from(externalServices).flatMap(
				([scriptName, { classNames }]) =>
					Array.from(classNames).map<[string, string]>((className) => [
						scriptName,
						className,
					])
			);

			// Bake initial registry state into the module source so the proxy
			// worker has the correct registry from the moment workerd loads it.
			const initialRegistry = this.#devRegistry.getRegistry();
			const mainModuleSource = [
				`import { ExternalQueueProxy, ExternalServiceProxy, setRegistry, createProxyDurableObjectClass } from "./dev-registry-proxy.worker.js";`,
				`export { ExternalQueueProxy, ExternalServiceProxy };`,
				`setRegistry(${JSON.stringify(initialRegistry)});`,
				`export default {`,
				`  async fetch(request, env) {`,
				`    if (request.method === "POST") {`,
				`      const data = await request.json();`,
				`      setRegistry(data);`,
				`      return new Response("ok");`,
				`    }`,
				`    return new Response("not found", { status: 404 });`,
				`  }`,
				`};`,
				...externalObjects.map(
					([scriptName, className]) =>
						`export const ${getOutboundDoProxyClassName(
							scriptName,
							className
						)} = createProxyDurableObjectClass({ scriptName: ${JSON.stringify(
							scriptName
						)}, className: ${JSON.stringify(className)} });`
				),
			].join("\n");

			services.set(getUserServiceName(SERVICE_DEV_REGISTRY_PROXY), {
				name: getUserServiceName(SERVICE_DEV_REGISTRY_PROXY),
				worker: {
					compatibilityDate: "2025-01-01",
					compatibilityFlags: ["service_binding_extra_handlers"],
					modules: [
						{
							name: "main.mjs",
							esModule: mainModuleSource,
						},
						{
							name: "dev-registry-proxy.worker.js",
							esModule: SCRIPT_DEV_REGISTRY_PROXY(),
						},
					],
					bindings: [
						{
							name: CoreBindings.DEV_REGISTRY_DEBUG_PORT,
							// workerdDebugPort bindings don't have any additional configuration
							workerdDebugPort: kVoid,
						},
						{
							name: CoreBindings.DEV_REGISTRY_INSTANCE_ID,
							text: this.#devRegistry.instanceId,
						},
						WORKER_BINDING_SERVICE_LOOPBACK,
					],
					durableObjectStorage: { inMemory: kVoid },
					// uniqueKey must match the target session's key for identical DO IDs.
					durableObjectNamespaces:
						externalObjects.map<Worker_DurableObjectNamespace>(
							([scriptName, className]) => ({
								className: getOutboundDoProxyClassName(scriptName, className),
								uniqueKey: `${scriptName}-${className}`,
							})
						),
				},
			});

			// Give the dev-registry-proxy service its own HTTP socket so
			// #pushRegistryUpdate can POST directly to it, bypassing the entry
			// worker.  On Windows the internal Cap'n Proto service-to-service
			// forwarding from the entry worker can break (WSARecv error 64).
			sockets.push({
				name: SOCKET_DEV_REGISTRY,
				address: "127.0.0.1:0",
				service: { name: getUserServiceName(SERVICE_DEV_REGISTRY_PROXY) },
				http: {},
			});
		}

		const globalServices = getGlobalServices({
			sharedOptions: sharedOpts,
			allWorkerRoutes,
			/*
			 * - if Workers + Assets project but NOT Vitest, the fallback Worker (see
			 *   `MINIFLARE_USER_FALLBACK`) should point to the (assets) RPC Proxy Worker
			 * - if Vitest with assets, the fallback Worker should point to the Vitest
			 *   runner Worker, while the SELF binding on the test runner will point to
			 *   the (assets) RPC Proxy Worker
			 *
			 * The prefix below must stay in sync with `WORKER_NAME_PREFIX` in
			 * `@cloudflare/vitest-plugin` (`src/pool/helpers.ts`), which names runner
			 * Workers `${WORKER_NAME_PREFIX}runner-<project>`.
			 */
			fallbackWorkerName:
				this.#workerOpts[0].config.assets &&
				!this.#workerOpts[0].config.name.startsWith("vitest-plugin-runner-")
					? `${RPC_PROXY_SERVICE_NAME}:${this.#workerOpts[0].config.name}`
					: getUserServiceName(this.#workerOpts[0].config.name),
			tmpPath: this.#tmpPath,
			log: this.#log,
			proxyBindings,
			durableObjectClassNames,
			allWorkerOpts,
		});
		for (const service of globalServices) {
			// Global services should all have unique names
			assert(service.name !== undefined && !services.has(service.name));
			services.set(service.name, service);
		}

		const servicesArray = Array.from(services.values());

		return {
			services: servicesArray,
			sockets,
			extensions,
			// Structured logging is always enabled: workerd emits structured logs
			// that Miniflare parses and either forwards to a `handleStructuredLogs`
			// handler or writes to the console by default.
			structuredLogging: true,
			autogates: process.env.MINIFLARE_WORKERD_AUTOGATES
				? process.env.MINIFLARE_WORKERD_AUTOGATES.split(" ")
				: [],
			v8Flags: process.env.MINIFLARE_WORKERD_V8_FLAGS
				? process.env.MINIFLARE_WORKERD_V8_FLAGS.split(" ")
				: [],
		};
	}

	#handleWorkerdCrash(): void {
		this.#workerdCrashCount++;
		// Recovery used to be entirely silent, which made a crash look like an
		// unexplained dev server restart. Always say something: any crash is a
		// bug worth reporting, and the count distinguishes a one-off from a loop.
		this.#log.warn(
			`The Workers runtime crashed unexpectedly and is being restarted (crash #${
				this.#workerdCrashCount
			}). ` + "Any additional runtime output above may indicate the cause."
		);
		// A crash destroys the proxy server heap just like a config update.
		this.#proxyClient?.poisonProxies();
		// The runtime behind this candidate is gone. Withdraw before restarting so
		// peers can take ownership if recovery stalls; successful assembly registers
		// this instance again with its new debug-port address.
		this.#devRegistry.unregisterWorkers();
		void this.#runtimeMutex
			.runWith(async () => {
				try {
					await this.#assembleAndUpdateConfig(true);
				} catch (error) {
					// Same reasoning as the failed-update path in `#setOptions()`: there
					// is no longer a runtime behind what we advertised, so withdraw it
					// instead of leaving peers pointed at a dead debug port.
					this.#devRegistry.unregisterWorkers();
					const cause =
						error instanceof Error ? error : new Error(String(error));
					this.#runtimeRestartError = new MiniflareCoreError(
						"ERR_RUNTIME_FAILURE",
						"The Workers runtime failed to restart after an unexpected crash.",
						cause
					);
					throw this.#runtimeRestartError;
				}
			})
			.then(
				async () => {
					if (this.#disposeController.signal.aborted) {
						return;
					}
					try {
						await this.#sharedOpts.unsafeHandleRuntimeRestart?.();
					} catch (error) {
						const cause =
							error instanceof Error ? error : new Error(String(error));
						this.#log.error(
							new Error(
								"The Workers runtime restarted, but the runtime restart callback failed.",
								{ cause }
							)
						);
					}
				},
				(error) => {
					if (this.#disposeController.signal.aborted) {
						return;
					}
					this.#log.error(
						error instanceof Error ? error : new Error(String(error))
					);
				}
			);
	}

	async #assembleAndUpdateConfig(reusePorts = false) {
		await this.#closeBrowserProcesses();

		// This function must be run with `#runtimeMutex` held
		const initial = !this.#runtimeEntryURL;
		assert(this.#runtime !== undefined);
		const runtime = this.#runtime;
		const configuredHost = this.#sharedOpts.host ?? DEFAULT_HOST;
		// For internal loopback communication with workerd, always use 127.0.0.1
		// when localhost is configured. This prevents IPv6/IPv4 mismatch issues
		// where Node.js binds to [::1] but workerd resolves localhost to 127.0.0.1.
		// See: https://github.com/cloudflare/workers-sdk/issues/12910
		const loopbackHost =
			resolveLocalhost(configuredHost) ??
			maybeGetLocallyAccessibleHost(configuredHost) ??
			getURLSafeHost(configuredHost);
		const loopbackPort = await this.#getLoopbackPort();
		const config = await this.#assembleConfig(
			loopbackHost,
			loopbackPort,
			reusePorts
		);
		const configBuffer = serializeConfig(config);

		// Get all socket names we expect to get ports for
		assert(config.sockets !== undefined);
		const requiredSockets: SocketIdentifier[] = config.sockets.map(
			({ name }) => {
				assert(name !== undefined);
				return name;
			}
		);
		if (this.#sharedOpts.inspectorPort !== undefined) {
			requiredSockets.push(kInspectorSocket);
		}
		if (this.#devRegistry.isEnabled()) {
			requiredSockets.push(SOCKET_DEBUG_PORT);
			// SOCKET_DEV_REGISTRY is already in config.sockets (and therefore
			// requiredSockets) when external services or queues are configured.
			// Don't add it unconditionally — if neither exists, the socket isn't
			// defined and waiting for it would hang.
		}

		// Reload runtime
		const entryAddress = this.#getSocketAddress(
			SOCKET_ENTRY,
			this.#previousSharedOpts?.port,
			configuredHost,
			this.#sharedOpts.port,
			reusePorts
		);
		let runtimeInspectorAddress: string | undefined;
		if (this.#sharedOpts.inspectorPort !== undefined) {
			let runtimeInspectorPort = this.#sharedOpts.inspectorPort;
			if (this.#maybeInspectorProxyController !== undefined) {
				// if we have an inspector proxy let's use a
				// random port for the actual runtime inspector
				runtimeInspectorPort = 0;
			}
			runtimeInspectorAddress = this.#getSocketAddress(
				kInspectorSocket,
				this.#previousRuntimeInspectorPort,
				// Use "127.0.0.1" explicitly instead of "localhost" to avoid
				// DNS resolution issues. On systems where getaddrinfo("localhost")
				// returns ::1 but IPv6 is disabled, workerd fails to bind the
				// inspector socket and silently continues without emitting the
				// listen-inspector event, causing waitForPorts() to hang.
				// See https://github.com/cloudflare/workers-sdk/issues/14077
				"127.0.0.1",
				runtimeInspectorPort,
				reusePorts
			);
			this.#previousRuntimeInspectorPort = runtimeInspectorPort;
		}
		const loopbackAddress = `${loopbackHost}:${loopbackPort}`;
		const runtimeOpts: Abortable & RuntimeOptions = {
			signal: this.#disposeController.signal,
			entryAddress,
			loopbackAddress,
			requiredSockets,
			inspectorAddress: runtimeInspectorAddress,
			debugPortAddress: this.#devRegistry.isEnabled()
				? "127.0.0.1:0"
				: undefined,
			verbose: this.#sharedOpts.verbose,
			handleStructuredLogs: this.#sharedOpts.handleStructuredLogs,
			onWorkerdCrashRestart: () => this.#handleWorkerdCrash(),
			runtimeEnv: this.#sharedOpts.unsafeRuntimeEnv,
		};
		const maybeSocketPorts = await withPersistRootStartupLock(
			this.#sharedOpts.unsafeEnableSharedStorage
				? this.#sharedOpts.resourcePersistencePath
				: undefined,
			() =>
				runtime.updateConfig(
					configBuffer,
					runtimeOpts,
					this.#workerOpts.map((w) => w.config.name),
					this.#disposeController.signal
				)
		);
		if (this.#disposeController.signal.aborted) {
			return;
		}
		if (maybeSocketPorts === undefined) {
			throw new MiniflareCoreError(
				"ERR_RUNTIME_FAILURE",
				"The Workers runtime failed to start. " +
					"There is likely additional logging output above."
			);
		}
		// Note: `updateConfig()` doesn't resolve until ports for all required
		// sockets have been recorded. At this point, `maybeSocketPorts` contains
		// all of `requiredSockets` as keys.
		this.#socketPorts = maybeSocketPorts;

		if (
			this.#maybeInspectorProxyController !== undefined &&
			this.#sharedOpts.inspectorPort !== undefined
		) {
			// Try to get inspector port for the workers
			const maybePort = this.#socketPorts.get(kInspectorSocket);
			if (maybePort === undefined) {
				throw new MiniflareCoreError(
					"ERR_RUNTIME_FAILURE",
					"Unable to access the runtime inspector socket."
				);
			} else {
				await this.#maybeInspectorProxyController.updateConnection(
					this.#sharedOpts.inspectorPort,
					this.#sharedOpts.inspectorHost ?? "127.0.0.1",
					maybePort,
					this.#workerNamesToProxy()
				);
			}
		}

		const entrySocket = config.sockets?.[0];
		const secure = entrySocket !== undefined && "https" in entrySocket;
		const previousEntryURL = this.#runtimeEntryURL;

		const entryPort = maybeSocketPorts.get(SOCKET_ENTRY);
		assert(entryPort !== undefined);

		const maybeAccessibleHost = maybeGetLocallyAccessibleHost(configuredHost);
		if (maybeAccessibleHost === undefined) {
			// If the configured host wasn't locally accessible, we should've configured a 2nd local entry socket that is
			const localEntryPort = maybeSocketPorts.get(SOCKET_ENTRY_LOCAL);
			assert(localEntryPort !== undefined, "Expected local entry socket port");
			this.#runtimeEntryURL = new URL(`http://127.0.0.1:${localEntryPort}`);
		} else {
			this.#runtimeEntryURL = new URL(
				`${secure ? "https" : "http"}://${maybeAccessibleHost}:${entryPort}`
			);
		}

		if (previousEntryURL?.toString() !== this.#runtimeEntryURL.toString()) {
			// Close the previous dispatcher if the entry URL changed, to avoid
			// leaking sockets from the old Pool.
			void this.#runtimeDispatcher?.close().catch(() => {});
			this.#runtimeDispatcher = new Pool(this.#runtimeEntryURL, {
				connect: { rejectUnauthorized: false },
				// Disable timeouts for local dev — long-running responses (streaming,
				// slow uploads, long-polling) should not be killed by undici defaults.
				headersTimeout: 0,
				bodyTimeout: 0,
			});
		}

		// Set up a direct dispatcher to the dev-registry-proxy socket so we can
		// push registry updates without routing through the entry worker.
		// Only close/recreate when the port actually changes, to avoid tearing
		// down the connection pool while a #pushRegistryUpdate is in-flight.
		const devRegistryPort = maybeSocketPorts.get(SOCKET_DEV_REGISTRY);
		if (devRegistryPort !== this.#devRegistryPort) {
			void this.#devRegistryDispatcher?.close().catch(() => {});
			this.#devRegistryPort = devRegistryPort;
			if (devRegistryPort !== undefined) {
				this.#devRegistryDispatcher = new Pool(
					new URL(`http://127.0.0.1:${devRegistryPort}`)
				);
			} else {
				this.#devRegistryDispatcher = undefined;
			}
		}
		if (this.#proxyClient === undefined) {
			this.#proxyClient = new ProxyClient(
				this.#runtimeEntryURL,
				this.dispatchFetch
			);
		} else {
			// Update the proxy client with the new runtime URL to send requests to.
			// Existing proxies will already have been poisoned in `setOptions()`.
			this.#proxyClient.setRuntimeEntryURL(this.#runtimeEntryURL);
		}

		await this.#registerWorkers();

		// Catch any registry updates that occurred while workerd was booting.
		if (this.#devRegistry.isEnabled()) {
			await this.#queueRegistryUpdate();
		}

		if (!this.#runtimeMutex.hasWaiting) {
			// Only log and trigger reload if there aren't pending updates
			const ready = initial ? "Ready" : "Updated and ready";

			const urlSafeHost = getURLSafeHost(configuredHost);
			if (this.#sharedOpts.logRequests) {
				this.#log.logReady(
					`${ready} on ${green(
						`${secure ? "https" : "http"}://${urlSafeHost}:${entryPort}`
					)}`
				);
			}

			if (initial && this.#sharedOpts.logRequests) {
				const hosts: string[] = [];
				if (configuredHost === "::" || configuredHost === "*") {
					hosts.push("localhost");
					hosts.push("[::1]");
				}
				if (
					configuredHost === "::" ||
					configuredHost === "*" ||
					configuredHost === "0.0.0.0"
				) {
					hosts.push(...getAccessibleHosts(true));
				}

				for (const h of hosts) {
					this.#log.logReady(
						`- ${secure ? "https" : "http"}://${h}:${entryPort}`
					);
				}
			}

			this.#handleReload();
		}
		this.#runtimeRestartError = undefined;
	}

	async #closeBrowserProcesses() {
		const browsers = Array.from(this.#browserProcesses.values());
		this.#browserProcesses.clear();
		await Promise.all(
			browsers.map(({ browserProcess, wsEndpoint }) =>
				closeBrowserProcess(browserProcess, wsEndpoint)
			)
		);
	}

	async #waitForReady(disposing = false) {
		// If `#init()` threw, we'd like to propagate the error here, so `await` it.
		// Note we can't use `async`/`await` with getters. We'd also like to wait
		// for `setOptions` calls to complete before resolving.
		await this.#initPromise;
		// We'd also like to wait for `setOptions` calls to complete before, so wait
		// for runtime mutex to drain (i.e. all options updates applied).
		// (NOTE: can't just repeatedly wait on the mutex as use the presence of
		// waiters on the mutex to avoid logging ready/updated messages to the
		// console if there are future updates)
		await this.#runtimeMutex.drained();
		if (!disposing && this.#runtimeRestartError !== undefined) {
			throw this.#runtimeRestartError;
		}
		// If we called `dispose()`, we may not have a `#runtimeEntryURL` if we
		// `dispose()`d synchronously, immediately after constructing a `Miniflare`
		// instance. In this case, return a discard URL which we'll ignore.
		if (disposing) {
			return new URL("http://[100::]/");
		}
		// if there is an inspector proxy let's wait for it to be ready
		await this.#maybeInspectorProxyController?.ready;
		// Make sure `dispose()` wasn't called in the time we've been waiting
		this.#checkDisposed();
		// `#runtimeEntryURL` is assigned in `#assembleAndUpdateConfig()`, which is
		// called by `#init()`, and `#initPromise` doesn't resolve until `#init()`
		// returns.
		assert(this.#runtimeEntryURL !== undefined);
		// Return a copy so external mutations don't propagate to `#runtimeEntryURL`
		return new URL(this.#runtimeEntryURL.toString());
	}

	async #registerWorkers(): Promise<void> {
		if (!this.#devRegistry.isEnabled()) {
			return;
		}

		const debugPort = this.#socketPorts?.get(SOCKET_DEBUG_PORT);
		if (debugPort === undefined) {
			this.#log.warn(
				"Debug port not available — skipping dev registry registration"
			);
			// Nothing we advertised before is reachable, so withdraw it rather than
			// leaving peers pointed at an address we can no longer serve.
			this.#devRegistry.unregisterWorkers();
			return;
		}
		const debugPortAddress = `127.0.0.1:${debugPort}`;

		assert(
			this.#runtimeEntryURL !== undefined,
			"Runtime entry URL must be set before registering workers"
		);

		const entries: [string, WorkerDefinition][] = [];
		const storageScope = this.#sharedOpts.unsafeEnableSharedStorage
			? getStorageScope(this.#sharedOpts.resourcePersistencePath)
			: undefined;
		if (storageScope !== undefined) {
			entries.push([
				getStorageCandidateName(this.#devRegistry.instanceId),
				{
					debugPortAddress,
					defaultEntrypointService: "",
					userWorkerService: "",
					storageScope,
				},
			]);
		}
		for (const workerOpts of this.#workerOpts) {
			const workerName = workerOpts.config.name;
			if (!workerName || !workerOpts.dev?.unsafeRegisterWorker) {
				continue;
			}

			let defaultEntrypointService: string;
			if (workerOpts.dev?.unsafeOverrideFetchWorker) {
				defaultEntrypointService = getUserServiceName(
					workerOpts.dev.unsafeOverrideFetchWorker
				);
			} else if (workerOpts.config.assets) {
				defaultEntrypointService = `${RPC_PROXY_SERVICE_NAME}:${workerName}`;
			} else {
				defaultEntrypointService = getUserServiceName(workerName);
			}

			// Advertise consumed queues so producers in other dev sessions can
			// route messages for them to this process's queue broker (see
			// `ExternalQueueProxy`).
			const queueConsumers = getTriggersOfType(workerOpts.config, "queue").map(
				(trigger) => trigger.name
			);

			entries.push([
				workerName,
				{
					debugPortAddress,
					defaultEntrypointService,
					userWorkerService: getUserServiceName(workerName),
					...(queueConsumers.length > 0 ? { queueConsumers } : {}),
				},
			]);
		}

		this.#devRegistry.register(Object.fromEntries(entries));
	}

	get ready(): Promise<URL> {
		return this.#waitForReady();
	}

	async getCf(): Promise<Record<string, any>> {
		this.#checkDisposed();
		await this.ready;

		return JSON.parse(JSON.stringify(this.#cfObject));
	}

	async getInspectorURL(): Promise<URL> {
		this.#checkDisposed();
		await this.ready;

		if (this.#maybeInspectorProxyController !== undefined) {
			return this.#maybeInspectorProxyController.getInspectorURL();
		}

		// `#socketPorts` is assigned in `#assembleAndUpdateConfig()`, which is
		// called by `#init()`, and `ready` doesn't resolve until `#init()` returns
		assert(this.#socketPorts !== undefined);

		// Try to get inspector port for worker
		const maybePort = this.#socketPorts.get(kInspectorSocket);
		if (maybePort === undefined) {
			throw new TypeError(
				"Inspector not enabled in Miniflare instance. " +
					"Set the `inspectorPort` option to enable it."
			);
		}

		return new URL(`ws://127.0.0.1:${maybePort}`);
	}

	async unsafeGetDirectURL(
		workerName?: string,
		entrypoint = "default"
	): Promise<URL> {
		this.#checkDisposed();
		await this.#waitForReady();

		// Get worker index and options from name, defaulting to entrypoint
		const workerIndex = this.#findAndAssertWorkerIndex(workerName);
		const workerOpts = this.#workerOpts[workerIndex];

		// Try to get direct access port for worker
		const socketName = getDirectSocketName(workerIndex, entrypoint);
		// `#socketPorts` is assigned in `#assembleAndUpdateConfig()`, which is
		// called by `#init()`, and `ready` doesn't resolve until `#init()` returns.
		assert(this.#socketPorts !== undefined);
		const maybePort = this.#socketPorts.get(socketName);
		if (maybePort === undefined) {
			const friendlyWorkerName =
				workerName === undefined ? "entrypoint" : JSON.stringify(workerName);
			const friendlyEntrypointName =
				entrypoint === "default" ? entrypoint : JSON.stringify(entrypoint);
			throw new TypeError(
				`Direct access disabled in ${friendlyWorkerName} worker for ${friendlyEntrypointName} entrypoint`
			);
		}

		// Construct accessible URL from configured host and port
		const directSocket = workerOpts.dev?.unsafeDirectSockets?.find(
			(socket) => (socket.entrypoint ?? "default") === entrypoint
		);
		// Should be able to find socket with correct entrypoint if port assigned
		assert(directSocket !== undefined);
		const host = directSocket.host ?? DEFAULT_HOST;
		const accessibleHost =
			maybeGetLocallyAccessibleHost(host) ?? getURLSafeHost(host);
		// noinspection HttpUrlsUsage
		return new URL(`http://${accessibleHost}:${maybePort}`);
	}

	#checkDisposed() {
		if (this.#disposeController.signal.aborted) {
			throw new MiniflareCoreError(
				"ERR_DISPOSED",
				"Cannot use disposed instance"
			);
		}
	}

	async #setOptions(opts: MiniflareOptions) {
		// This function must be run with `#runtimeMutex` held

		// Split and validate options
		const [initialSharedOpts, workerOpts] = validateOptions(opts);
		let sharedOpts = initialSharedOpts;
		if (
			sharedOpts.unsafeEnableSharedStorage &&
			sharedOpts.resourcePersistencePath !== undefined
		) {
			sharedOpts = {
				...sharedOpts,
				resourcePersistencePath: await canonicalisePersistRoot(
					sharedOpts.resourcePersistencePath
				),
			};
		}
		this.#previousSharedOpts = this.#sharedOpts;
		this.#previousWorkerOpts = this.#workerOpts;
		this.#sharedOpts = sharedOpts;
		this.#workerOpts = workerOpts;
		this.#log = this.#sharedOpts.log ?? this.#log;
		this.#hyperdriveProxyController.log = this.#log;

		const newExternalOnUpdate = sharedOpts.unsafeHandleDevRegistryUpdate;
		await this.#devRegistry.updateRegistryPath(
			sharedOpts.unsafeDevRegistryPath,
			(registry) => {
				void this.#queueRegistryUpdate();
				newExternalOnUpdate?.(registry);
			}
		);
		// Send to runtime and wait for updates to process
		try {
			await this.#assembleAndUpdateConfig();
		} catch (error) {
			// The runtime this instance was reachable on has already been stopped by
			// this point, and we never got as far as advertising its replacement. Our
			// entries would otherwise sit in the registry pointing at a dead debug
			// port, kept fresh by their heartbeats so the stale-entry sweep never
			// reclaims them.
			this.#devRegistry.unregisterWorkers();
			throw error;
		}
	}

	setOptions(opts: MiniflareOptions): Promise<void> {
		this.#checkDisposed();
		// The `ProxyServer` "heap" will be destroyed when `workerd` restarts,
		// invalidating all existing native references. Mark all proxies as invalid.
		this.#proxyClient?.poisonProxies();
		// Wait for initial initialisation and other setOptions to complete before
		// changing options
		return this.#runtimeMutex.runWith(() => this.#setOptions(opts));
	}

	dispatchFetch: DispatchFetch = async (input, init) => {
		this.#checkDisposed();
		await this.ready;

		assert(this.#runtimeEntryURL !== undefined);
		assert(this.#runtimeDispatcher !== undefined);

		const forward = new Request(input, init);
		const url = new URL(forward.url);
		const actualRuntimeOrigin = this.#runtimeEntryURL.origin;
		const userRuntimeOrigin = url.origin;

		// Rewrite URL for WebSocket requests which won't use `DispatchFetchDispatcher`
		url.protocol = this.#runtimeEntryURL.protocol;
		url.host = this.#runtimeEntryURL.host;

		// Remove `Content-Length: 0` headers from requests when a body is set to
		// avoid `RequestContentLengthMismatch` errors
		if (
			forward.body !== null &&
			forward.headers.get("Content-Length") === "0"
		) {
			forward.headers.delete("Content-Length");
		}

		const cfBlob = forward.cf ? { ...fallbackCf, ...forward.cf } : undefined;
		const dispatcher = new DispatchFetchDispatcher(
			getGlobalDispatcher(),
			this.#runtimeDispatcher,
			actualRuntimeOrigin,
			userRuntimeOrigin,
			cfBlob
		);

		const forwardInit = forward as RequestInit;
		forwardInit.dispatcher = dispatcher;
		const response = await fetch(url, forwardInit);

		// If the Worker threw an uncaught exception, propagate it to the caller
		const stack = response.headers.get(CoreHeaders.ERROR_STACK);
		if (response.status === 500 && stack !== null) {
			// `workerd` drops response bodies for `HEAD` requests, so fall back to
			// the header copy of the serialised error
			const serialised =
				(await response.text()) || decodeErrorPayload(response);
			if (serialised === null) {
				throw new Error("Worker threw an uncaught exception");
			}
			const caught = JsonErrorSchema.parse(JSON.parse(serialised));
			throw reviveError(this.#workerOpts, caught);
		}

		// At this point, undici.fetch (used inside fetch, above)
		// has decompressed the response body but retained the Content-Encoding header.
		// This can cause problems for client implementations which rely
		// on the Content-Encoding header rather than trying to infer it from the body.
		// Technically, at this point, this a malformed response so let's remove the header
		// Retain it as MF-Content-Encoding so we can tell the body was actually compressed.
		const contentEncoding = response.headers.get("Content-Encoding");
		if (contentEncoding) {
			response.headers.set("MF-Content-Encoding", contentEncoding);
		}
		response.headers.delete("Content-Encoding");

		if (
			process.env.MINIFLARE_ASSERT_BODIES_CONSUMED === "true" &&
			response.body !== null
		) {
			// Throw an uncaught exception if the body from this response isn't
			// consumed "immediately". `undici` may hang or throw socket errors if we
			// don't remember to do this:
			// https://github.com/nodejs/undici/issues/583#issuecomment-1577468249
			const originalLimit = Error.stackTraceLimit;
			Error.stackTraceLimit = Infinity;
			const error = new Error(
				"`body` returned from `Miniflare#dispatchFetch()` not consumed immediately"
			);
			Error.stackTraceLimit = originalLimit;
			setImmediate(() => {
				if (!response.bodyUsed) {
					throw error;
				}
			});
		}

		return response;
	};

	/**
	 * Opens a TCP connection to a Worker's connect trigger.
	 *
	 * @param options Worker and trigger selection options
	 * @returns A connected Node.js socket
	 */
	dispatchConnect = async (
		options: DispatchConnectOptions = {}
	): Promise<net.Socket> => {
		this.#checkDisposed();
		await this.ready;

		const workerIndex = this.#findAndAssertWorkerIndex(options.workerName);
		const workerOpts = this.#workerOpts[workerIndex];
		const connectTriggers = getTriggersOfType(workerOpts.config, "connect");
		const workerDescription =
			options.workerName === undefined
				? "entrypoint worker"
				: `${JSON.stringify(options.workerName)} worker`;

		let trigger: (typeof connectTriggers)[number] | undefined;
		if (options.port === undefined) {
			if (connectTriggers.length === 0) {
				throw new TypeError(
					`No TCP connect triggers configured for ${workerDescription}`
				);
			}
			if (connectTriggers.length > 1) {
				throw new TypeError(
					`Multiple TCP connect triggers configured for ${workerDescription}; specify a port`
				);
			}
			trigger = connectTriggers[0];
		} else {
			trigger = connectTriggers.find(({ port }) => port === options.port);
			if (trigger === undefined) {
				throw new TypeError(
					`TCP connect trigger on port ${options.port} not found for ${workerDescription}`
				);
			}
		}

		assert(this.#socketPorts !== undefined);
		const socketName = getConnectSocketName(
			workerIndex,
			trigger.protocol,
			trigger.port
		);
		const port = this.#socketPorts.get(socketName);
		assert(port !== undefined);

		const configuredHost = trigger.address ?? DEFAULT_HOST;
		const host =
			resolveLocalhost(configuredHost) ??
			(configuredHost === "*" ||
			configuredHost === "0.0.0.0" ||
			configuredHost === "::"
				? DEFAULT_HOST
				: configuredHost);
		const socket = net.connect({ host, port });
		this.#dispatchConnectSockets.add(socket);
		socket.once("close", () => this.#dispatchConnectSockets.delete(socket));

		try {
			await new Promise<void>((resolve, reject) => {
				function cleanup() {
					socket.off("connect", onConnect);
					socket.off("error", onError);
					socket.off("close", onClose);
				}
				function onConnect() {
					cleanup();
					resolve();
				}
				function onError(error: Error) {
					cleanup();
					reject(error);
				}
				function onClose() {
					cleanup();
					reject(new Error("Socket closed before connecting"));
				}

				socket.once("connect", onConnect);
				socket.once("error", onError);
				socket.once("close", onClose);
			});
		} catch (error) {
			socket.destroy();
			throw error;
		}

		return socket;
	};

	/** @internal */
	async _getProxyClient(): Promise<ProxyClient> {
		this.#checkDisposed();
		await this.ready;
		assert(this.#proxyClient !== undefined);
		return this.#proxyClient;
	}

	#findAndAssertWorkerIndex(workerName?: string): number {
		if (workerName === undefined) {
			return 0;
		} else {
			const index = this.#workerOpts.findIndex(
				({ config }) => config.name === workerName
			);
			if (index === -1) {
				throw new TypeError(`${JSON.stringify(workerName)} worker not found`);
			}
			return index;
		}
	}

	async getBindings<Env = Record<string, unknown>>(
		workerName?: string
	): Promise<Env> {
		const bindings: Record<string, unknown> = {};
		const proxyClient = await this._getProxyClient();

		// Find worker by name, defaulting to entrypoint worker if none specified
		const workerIndex = this.#findAndAssertWorkerIndex(workerName);
		const workerOpts = this.#workerOpts[workerIndex];
		workerName = workerOpts.config.name;

		// Populate bindings from each plugin
		for (const [key, plugin] of this.#mergedPluginEntries) {
			const pluginBindings = await plugin.getNodeBindings(workerOpts);
			for (const [name, binding] of Object.entries(pluginBindings)) {
				if (binding instanceof ProxyNodeBinding) {
					const proxyBindingName = getProxyBindingName(key, workerName, name);
					let proxy = proxyClient.env[proxyBindingName];
					assert(
						proxy !== undefined,
						`Expected ${proxyBindingName} to be bound`
					);
					if (binding.proxyOverrideHandler) {
						proxy = new Proxy(proxy, binding.proxyOverrideHandler);
					}
					bindings[name] = proxy;
				} else {
					bindings[name] = binding;
				}
			}
		}

		return bindings as Env;
	}
	async getWorker(workerName?: string): Promise<ReplaceWorkersTypes<Fetcher>> {
		const proxyClient = await this._getProxyClient();

		// Find worker by name, defaulting to entrypoint worker if none specified
		const workerIndex = this.#findAndAssertWorkerIndex(workerName);
		const workerOpts = this.#workerOpts[workerIndex];
		workerName = workerOpts.config.name;

		// Get a `Fetcher` to that worker (NOTE: the `ProxyServer` Durable Object
		// shares its `env` with Miniflare's entry worker, so has access to routes)
		const bindingName = CoreBindings.SERVICE_USER_ROUTE_PREFIX + workerName;

		const fetcher = proxyClient.env[bindingName];
		// `#findAndAssertWorkerIndex()` throws if a worker doesn't exist with the
		// specified name, so by this point the worker is guaranteed to have a
		// corresponding route service binding.
		assert(
			fetcher !== undefined,
			`Expected ${bindingName} service binding for worker ${JSON.stringify(
				workerName
			)}`
		);
		return fetcher as ReplaceWorkersTypes<Fetcher>;
	}

	async #getProxy<T>(
		pluginName: string,
		bindingName: string,
		workerName?: string
	): Promise<T> {
		const proxyClient = await this._getProxyClient();
		const resolvedWorkerName = workerName ?? this.#workerOpts[0].config.name;
		const proxyBindingName = getProxyBindingName(
			pluginName,
			resolvedWorkerName,
			bindingName
		);
		const proxy = proxyClient.env[proxyBindingName];
		if (proxy === undefined) {
			// If the user specified an invalid binding/worker name, throw
			const friendlyWorkerName = resolvedWorkerName
				? `${JSON.stringify(resolvedWorkerName)} worker`
				: "the worker";
			const bindingTypeDescription = Object.fromEntries(
				this.#mergedPluginEntries
			)[pluginName]?.bindingTypeDescription;
			const bindingType = bindingTypeDescription
				? `${bindingTypeDescription} binding`
				: "binding";
			throw new TypeError(
				`No ${bindingType} named ${JSON.stringify(
					bindingName
				)} found in ${friendlyWorkerName}.`
			);
		}
		return proxy as T;
	}

	/**
	 * Returns remote storage access for a Durable Object instance.
	 *
	 * Calling `exec()` runs SQL inside the target Durable Object and returns all
	 * rows. It may start the object if it is not already active.
	 */
	async unsafeGetDurableObjectStorage(
		scriptName: string,
		className: string,
		options: DurableObjectStorageOptions
	): Promise<DurableObjectStorageHandle> {
		if (!this.#sharedOpts.unsafeInspectDurableObjects) {
			throw new TypeError(
				"Durable Object storage inspection requires the `unsafeInspectDurableObjects` option."
			);
		}

		const control = await this.#getDevControl();
		const handle = createDurableObjectStorageHandle(
			control,
			scriptName,
			className,
			options
		);

		return handle;
	}
	// TODO(someday): would be nice to define these in plugins
	async getCaches(): Promise<ReplaceWorkersTypes<CacheStorage>> {
		const proxyClient = await this._getProxyClient();
		return proxyClient.global
			.caches as unknown as ReplaceWorkersTypes<CacheStorage>;
	}
	/**
	 * Purges all entries from the cache.
	 *
	 * This is useful during development when cached assets need to be cleared
	 * without restarting the Miniflare instance.
	 *
	 * @param cacheName - Optional name of specific cache to purge. If not provided,
	 *                    purges the default cache.
	 * @returns A promise that resolves with the number of entries purged.
	 */
	async purgeCache(cacheName?: string): Promise<number> {
		await this.ready;

		const proxyClient = await this._getProxyClient();

		// Get the cache service binding from proxy env. This is a Fetcher to
		// the cache-entry worker which properly sets cf.miniflare.name before
		// forwarding to the CacheObject Durable Object.
		const cacheService = proxyClient.env[CoreBindings.SERVICE_CACHE] as Fetcher;

		// Build the request with the cache namespace header if specified
		const headers: Record<string, string> = {};
		if (cacheName !== undefined) {
			headers[CacheHeaders.NAMESPACE] = cacheName;
		}

		// Call the cache-entry worker's /purge-all endpoint
		const response = await cacheService.fetch("http://localhost/purge-all", {
			method: "DELETE",
			headers,
		});

		if (!response.ok) {
			throw new Error(`Failed to purge cache: ${response.statusText}`);
		}

		const result = (await response.json()) as { deleted: number };
		return result.deleted;
	}
	getD1Database(bindingName: string, workerName?: string): Promise<D1Database> {
		return this.#getProxy(D1_PLUGIN_NAME, bindingName, workerName);
	}
	getDurableObjectNamespace(
		bindingName: string,
		workerName?: string
	): Promise<ReplaceWorkersTypes<DurableObjectNamespace>> {
		return this.#getProxy(DURABLE_OBJECTS_PLUGIN_NAME, bindingName, workerName);
	}
	async #getDevControl(): Promise<DevControl> {
		const proxyClient = await this._getProxyClient();
		const control = proxyClient.env[CoreBindings.SERVICE_DEV_CONTROL] as
			| DevControl
			| undefined;
		assert(control !== undefined, "Expected dev control service");

		return control;
	}
	async unsafeEvictDurableObject(
		scriptName: string,
		className: string,
		options: DurableObjectEvictionOptions
	): Promise<void> {
		this.#checkDisposed();
		await this.ready;

		const durableObjectExists = this.#workerOpts.some((workerOpts) => {
			// A Durable Object class is defined by the worker that exports it (a
			// `durable-object` export keyed by the class name).
			return (
				workerOpts.config.name === scriptName &&
				getExportsOfType(workerOpts.config, "durable-object").some(
					([exportName]) => exportName === className
				)
			);
		});

		if (!durableObjectExists) {
			throw new TypeError(
				`No Durable Object class named ${JSON.stringify(
					className
				)} found in ${JSON.stringify(scriptName)} worker.`
			);
		}

		const control = await this.#getDevControl();
		await control.evictDurableObject(scriptName, className, options);
	}
	async listDurableObjectIds(
		classNameOrBindingName: string,
		workerName?: string
	): Promise<string[]> {
		this.#checkDisposed();
		await this.ready;

		const workerIndex = this.#findAndAssertWorkerIndex(workerName);
		const workerOpts = this.#workerOpts[workerIndex];
		const resolvedWorkerName = workerOpts.config.name;

		// `classNameOrBindingName` may be either a namespace binding name (a
		// `durable-object` entry in `config.env`) or a class name exported by this
		// worker (a `durable-object` entry in `config.exports`).
		let className: string | undefined;
		let scriptName: string | undefined;
		const binding = workerOpts.config.env?.[classNameOrBindingName];
		if (binding?.type === "durable-object") {
			className = binding.exportName;
			scriptName = binding.worker;
		} else if (
			getExportsOfType(workerOpts.config, "durable-object").some(
				([exportName]) => exportName === classNameOrBindingName
			)
		) {
			className = classNameOrBindingName;
			scriptName = resolvedWorkerName;
		}

		if (className === undefined) {
			const friendlyWorkerName = resolvedWorkerName
				? `${JSON.stringify(resolvedWorkerName)} worker`
				: "the worker";
			throw new TypeError(
				`No Durable Object class or namespace binding named ${JSON.stringify(
					classNameOrBindingName
				)} found in ${friendlyWorkerName}.`
			);
		}

		const serviceName = getUserServiceName(scriptName ?? resolvedWorkerName);
		const classConfig = getDurableObjectClassNames(this.#workerOpts)
			.get(serviceName)
			?.get(className);
		const unsafeUniqueKey = classConfig?.unsafeUniqueKey;

		const namespaceKey = getDurableObjectUniqueKey(
			className,
			scriptName ?? resolvedWorkerName,
			unsafeUniqueKey
		);

		if (namespaceKey === undefined) {
			throw new TypeError(
				`Cannot list Durable Object ids for ${JSON.stringify(
					classNameOrBindingName
				)} because the namespace uses ephemeral local storage.`
			);
		}

		const durableObjectsPersistPath = getPersistPath(
			DURABLE_OBJECTS_PLUGIN_NAME,
			this.#tmpPath,
			this.#sharedOpts.isolatedResourcePersistencePath
		);

		try {
			const entries = await fs.promises.readdir(
				path.join(durableObjectsPersistPath, namespaceKey),
				{ withFileTypes: true }
			);

			const ids: string[] = [];
			for (const entry of entries) {
				const objectId = path.basename(entry.name, ".sqlite");
				if (
					entry.isFile() &&
					path.extname(entry.name) === ".sqlite" &&
					objectId !== "metadata"
				) {
					ids.push(objectId);
				}
			}

			return ids.sort();
		} catch (error) {
			if (isFileNotFoundError(error)) {
				return [];
			}

			throw error;
		}
	}
	getKVNamespace(
		bindingName: string,
		workerName?: string
	): Promise<ReplaceWorkersTypes<KVNamespace>> {
		return this.#getProxy(KV_PLUGIN_NAME, bindingName, workerName);
	}
	getSecretsStoreSecretAPI(
		bindingName: string,
		workerName?: string
	): Promise<() => SecretsStoreSecretAdmin> {
		return this.#getProxy(
			SECRET_STORE_PLUGIN_NAME,
			bindingName,
			workerName
		).then((binding) => {
			// @ts-expect-error We exposed an admin API on this key
			return binding[ADMIN_API];
		});
	}
	getSecretsStoreSecret(
		bindingName: string,
		workerName?: string
	): Promise<ReplaceWorkersTypes<KVNamespace>> {
		return this.#getProxy(SECRET_STORE_PLUGIN_NAME, bindingName, workerName);
	}
	getQueueProducer<Body = unknown>(
		bindingName: string,
		workerName?: string
	): Promise<Queue<Body>> {
		return this.#getProxy(QUEUES_PLUGIN_NAME, bindingName, workerName);
	}
	getR2Bucket(
		bindingName: string,
		workerName?: string
	): Promise<ReplaceWorkersTypes<R2Bucket>> {
		return this.#getProxy(R2_PLUGIN_NAME, bindingName, workerName);
	}
	getImagesBinding(
		bindingName: string,
		workerName?: string
	): Promise<ReplaceWorkersTypes<ImagesBinding>> {
		return this.#getProxy(IMAGES_PLUGIN_NAME, bindingName, workerName);
	}
	getHelloWorldBinding(
		bindingName: string,
		workerName?: string
	): Promise<{
		get: () => Promise<{ value: string; ms?: number }>;
		set: (value: string) => Promise<void>;
	}> {
		return this.#getProxy(HELLO_WORLD_PLUGIN_NAME, bindingName, workerName);
	}
	getFlagshipBinding(
		bindingName: string,
		workerName?: string
	): Promise<Flagship> {
		return this.#getProxy(FLAGSHIP_PLUGIN_NAME, bindingName, workerName);
	}
	getFlagshipBindingAPI(
		bindingName: string,
		workerName?: string
	): Promise<() => FlagshipAdmin> {
		return this.#getProxy(FLAGSHIP_PLUGIN_NAME, bindingName, workerName).then(
			(binding) => {
				// @ts-expect-error We exposed an admin API on this key
				return binding[FLAGSHIP_ADMIN_API];
			}
		);
	}
	getStreamBinding(
		bindingName: string,
		workerName?: string
	): Promise<StreamBinding> {
		return this.#getProxy(STREAM_PLUGIN_NAME, bindingName, workerName);
	}

	/** @internal */
	async _getInternalDurableObjectNamespace(
		pluginName: string,
		serviceName: string,
		className: string
	): Promise<ReplaceWorkersTypes<DurableObjectNamespace>> {
		if (
			this.#sharedOpts.unsafeEnableSharedStorage &&
			[
				D1_PLUGIN_NAME,
				IMAGES_PLUGIN_NAME,
				KV_PLUGIN_NAME,
				R2_PLUGIN_NAME,
				RATELIMIT_PLUGIN_NAME,
				SECRET_STORE_PLUGIN_NAME,
				STREAM_PLUGIN_NAME,
			].includes(pluginName)
		) {
			throw new TypeError(
				"Direct internal storage access is unavailable while shared storage is enabled"
			);
		}
		return this.#getProxy(`${pluginName}-internal`, className, serviceName);
	}

	get #mergedPluginEntries() {
		return [...PLUGIN_ENTRIES, ...this.#externalPlugins.entries()];
	}

	async dispose(): Promise<void> {
		this.#disposeController.abort();
		for (const socket of this.#dispatchConnectSockets) {
			socket.destroy();
		}
		this.#dispatchConnectSockets.clear();
		// The `ProxyServer` "heap" will be destroyed when `workerd` shuts down,
		// invalidating all existing native references. Mark all proxies as invalid.
		// Note `dispose()`ing the `#proxyClient` implicitly poison's proxies, but
		// we'd like them to be poisoned synchronously here.
		this.#proxyClient?.poisonProxies();
		// Preserve a readiness failure without allowing it to skip teardown.
		let waitForReadyFailed = false;
		let waitForReadyError: unknown;
		try {
			await this.#waitForReady(/* disposing */ true);
		} catch (error) {
			waitForReadyFailed = true;
			waitForReadyError = error;
		}

		// Runtime.dispose() requests workerd termination synchronously before
		// returning its child-exit promise. Start it before awaiting independent
		// cleanup so those hooks cannot delay or skip the termination request.
		let runtimeDisposePromise: Promise<void>;
		try {
			runtimeDisposePromise = Promise.resolve(this.#runtime?.dispose());
		} catch (error) {
			runtimeDisposePromise = Promise.reject(error);
		}
		// Attach a rejection handler immediately so a fast runtime failure cannot
		// become unhandled while independent cleanup is still pending.
		const runtimeDisposeOutcome = runtimeDisposePromise.then(
			() => ({ ok: true as const }),
			(error: unknown) => ({ ok: false as const, error })
		);

		// Preserve the existing first cleanup error, while still waiting for the
		// already-started runtime exit before the outer disposal settles.
		let independentCleanupFailed = false;
		let independentCleanupError: unknown;
		try {
			// Cleanup as much as possible even if `#init()` threw.
			await this.#closeBrowserProcesses();

			// Remove exit hook, we're cleaning up what they would've cleaned up now
			this.#removeExitHook?.();

			await this.#proxyClient?.dispose();
		} catch (error) {
			independentCleanupFailed = true;
			independentCleanupError = error;
		}

		const runtimeCleanupOutcome = await runtimeDisposeOutcome;
		this.#devRegistry.unregisterWorkers();
		try {
			await Promise.all(
				[...this.#pendingWorkflowStorageDeletes.values()].map(
					({ promise }) => promise
				)
			);
		} catch (error) {
			if (!independentCleanupFailed) {
				independentCleanupFailed = true;
				independentCleanupError = error;
			}
		}
		// Close the undici Pool used for dispatching fetch requests to the
		// runtime. This must happen after the runtime is disposed, so that
		// in-flight connections are broken and close immediately. Without this,
		// lingering sockets in the Pool can keep the Node.js event loop alive.
		// The Pool may already be destroyed (e.g., if workerd was SIGKILL'd and
		// all connections broke), so ignore ClientDestroyedError.
		try {
			await this.#runtimeDispatcher?.close();
		} catch {}
		// Also close the dev-registry dispatcher (same issue as above).
		try {
			await this.#devRegistryDispatcher?.close();
		} catch {}

		await this.#stopLoopbackServer();
		// Close the WebSocket server so any connected clients are disconnected
		// and their sockets don't keep the Node.js event loop alive. It uses
		// `noServer: true` so it doesn't own an HTTP server, but connected
		// WebSocket clients still hold open sockets.
		this.#webSocketServer.close();
		// Best-effort cleanup: on Windows, workerd may not release file handles
		// immediately after disposal, causing EBUSY errors. The temp directory
		// lives in os.tmpdir() so the OS will clean it up eventually.
		removeDir(this.#tmpPath, { fireAndForget: true });
		// Clean up email session directories in the project temp path. When no
		// project temp path is supplied, these live inside `#tmpPath` and are
		// already removed above.
		const emailPaths = getEmailPathsToClean(
			this.#sharedOpts.resourceTmpPath,
			this.#tmpPath
		);
		if (emailPaths) {
			try {
				await removeDir(emailPaths.sessionDir);
			} catch (e) {
				this.#log.debug(
					`Unable to remove email session directory: ${String(e)}`
				);
			}
		}

		// Close the inspector proxy server if there is one
		try {
			await this.#maybeInspectorProxyController?.dispose();
		} catch (error) {
			if (!independentCleanupFailed) {
				independentCleanupFailed = true;
				independentCleanupError = error;
			}
		}
		// Unregister workers from dev registry and stop the file watcher
		await this.#devRegistry.dispose();

		// shutdown hyperdrive proxies if any exist
		await this.#hyperdriveProxyController.dispose();

		// Remove from instance registry as last step in disposal, preserving the
		// existing behavior when an earlier cleanup operation fails.
		maybeInstanceRegistry?.delete(this);

		if (independentCleanupFailed) {
			throw independentCleanupError;
		}
		if (!runtimeCleanupOutcome.ok) {
			throw runtimeCleanupOutcome.error;
		}
		if (waitForReadyFailed) {
			throw waitForReadyError;
		}
	}
}

export type { WorkerdStructuredLog } from "./plugins/core";

export type { FlagshipAdmin } from "./workers/flagship/admin";

export type {
	BaseCondition,
	Condition,
	ErrorCode,
	LogicalCondition,
	EvaluationContext,
	EvaluationDetails,
	EvaluationReason,
	FlagValue,
	Operator,
	Rollout,
} from "./workers/flagship/evaluate";
export type {
	Flag,
	FlagChanges,
	FlagInput,
	FlagType,
	Rule,
} from "./workers/flagship/flags";

export interface SecretsStoreSecretAdmin {
	create(value: string): Promise<string>;
	update(value: string, id: string): Promise<string>;
	duplicate(id: string, newName: string): Promise<string>;
	delete(id: string): Promise<void>;
	list(): Promise<KVNamespaceListKey<{ uuid: string }, string>[]>;
	get(id: string): Promise<string>;
}

export * from "./http";
export * from "./plugins";
export * from "./runtime";
export * from "./shared";
export * from "./workers";
export * from "./merge";
export type {
	DurableObjectIdentifier,
	DurableObjectEvictionOptions,
	DurableObjectStorageOptions,
	DurableObjectStorageHandle,
} from "./shared/dev-control";
export type {
	WorkerRegistry,
	WorkerDefinition,
} from "./shared/dev-registry-types";
export {
	getDefaultDevRegistryPath,
	getWorkerRegistry,
} from "./shared/dev-registry";
export { parseModuleFallbackRequest } from "./plugins/core/module-fallback";
export type {
	V1ModuleFallbackRequest,
	V2ModuleFallbackRequest,
	ParsedModuleFallbackRequest,
} from "./plugins/core/module-fallback";
export {
	DevConfigSchema,
	InstanceOptionsSchema,
	LegacyConfigSchema,
	MiniflareDurableObjectExportSchema,
	MiniflareOptionsSchema,
	MiniflareWorkerConfigBaseSchema,
	MiniflareWorkerConfigSchema,
	ParsedMiniflareKnownBindingSchema,
	WorkerOptionsSchema,
} from "./config/schema";
export {
	V4MiniflareOptionsSchema,
	V4WorkerOptionsSchema,
} from "./config/v4-schema";
export { convertV4MiniflareOptions } from "./config/v4-convert";
export type {
	MiniflareOptions,
	WorkerOptions,
	MiniflareWorkerConfig,
	DevConfig,
	LegacyConfig,
	InstanceOptions,
} from "./config/schema";
export type {
	V4DurableObject,
	V4FetchHandler,
	V4IdEntry,
	V4MiniflareOptions,
	V4ModuleDefinition,
	V4ModuleRule,
	V4ModuleRuleType,
	V4Namespace,
	V4NodeHandler,
	V4QueueConsumerOptions,
	V4QueueProducerOptions,
	V4RemoteBinding,
	V4RemoteBindingWithName,
	V4SharedOptions,
	V4ServiceDesignator,
	V4SourceOptions,
	V4WorkerOptions,
	V4WorkerOptionsShape,
	V4WorkerdStructuredLog,
} from "./config/v4-schema";
